/**
 * Discovery Section Controller
 * Manages the Discovery page UI
 */

import page from '../vendor/page.js';
import { escapeHtml, eventBus, isGuestName } from '../core/utils.js';
import {
  emitContentStarred,
  emitContentArchived,
  emitContentDeleted,
  emitContentViewed,
  CONTENT_STARRED,
  CONTENT_ARCHIVED,
  CONTENT_DELETED
} from '../core/contentSync.js';
import { API } from '../core/api.js';
import { DiscoveryAPI } from '../core/discoveryApi.js';
import { StickyStack } from '../utils/StickyStack.js';
import { Tooltip } from '../utils/Tooltip.js';
import { goToUpgrade } from '../utils/upgradeNavigation.js';
import { PolarProgressChart } from '../components/PolarProgressChart.js';
import { PathOrbit } from '../components/PathOrbit.js';
import { loadTopicProgress } from '../core/progressData.js';
import {
  renderProgressAside,
  renderHeroAside,
  renderTopicRow,
  renderPathRow,
  bindTopicRows,
  setTopicRowHot
} from '../components/progressHero.js';
import { buildTopicDetailContent } from '../components/topicDetailPanelHelper.js';
import { OrientationBanner } from '../components/OrientationBanner.js';
import { ResultsFilterBar } from '../components/ResultsFilterBar.js';
import { renderEmptyState } from '../components/emptyState.js';
import { renderNavBar as renderSegmentNavBar, renderNavItem as renderSegmentNavItem } from '../components/segmentNav.js';
import { buildPageInfoPanel, renderPageInfoTitle, renderPageInfoFaq } from '../components/pageInfoPanel.js';
import { openSessionInPanel } from '../components/sessionDrillHelper.js';
import { buildGuideManagementDropdown } from '../components/guideOptionsHelper.js';
import { armGuideContentsSpy, renderGuideDetailBody, buildGuideContentsMenu } from '../components/guideDrillHelper.js';
import { renderRowKebab, mountSelectToolbar, RowMenuController } from '../components/optionsMenu.js';
import { renderSectionChecklist, bindSectionChecklist } from '../components/sectionChecklist.js';
import { renderDisclosure, wireDisclosures } from '../components/disclosure.js';
import { threadTitleText, discussThread, buildThreadDetailPayload } from '../components/threadDrillHelper.js';
import { formatRelativeDate } from '../utils/timeFormat.js';
import { notifyArchived, notifyArchiveFailed } from '../utils/archiveFeedback.js';
import { enterTopicDiscovery } from '../core/topicDiscoveryEntry.js';
import { modalService } from '../services/ModalService.js';

// Local star glyph for the Open Threads select-mode toolbar.
const STAR_SVG = `<svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><use href="#icon-star"/></svg>`;

/**
 * Disclosure key for the path detail's progress zone ("Current progress" /
 * "Sections we'll cover"), so `_loadPathSectionsList` can find the header and
 * fill in its chip once the section count is known.
 */
const PATH_PROGRESS_ZONE = 'path-progress';

const NAV_ITEM_SELECTOR = '.page-toggle__btn';
const NAV_ACTIVE_CLASS = 'page-toggle__btn--active';

/** Row selector + dataset key the Paths tab passes to the shared row binders. */
const PATH_ROW_BINDING = Object.freeze({
  rowSelector: '.discovery-path-row[data-path-id]',
  idKey: 'pathId'
});

/** Fallback centre of the orbit when no path is current (everything done). */
const ORBIT_CENTER_PATH_ID = 'foundation';

/** Guide states that count as earned — a stale guide was still earned. */
const EARNED_GUIDE_STATUSES = Object.freeze(['READY', 'STALE']);

/**
 * The joiner between clauses of a path row's one metadata line. Every state's
 * line is built from the same separator so the three rungs (scope → progress →
 * completion) read as one column of facts rather than three formats.
 */
const ROW_FACT_SEPARATOR = ' · ';

/**
 * `data-action` of the "See Premium" pill on the Paths sheet's "Available with
 * Premium" group label — the sheet's one upgrade affordance (PATH ACCESS).
 * @type {string}
 */
const PATH_GROUP_UPGRADE_ACTION = 'path-group-upgrade';

export class DiscoverySection {
  /**
   * URL parameter schema for deep linking and filter state
   * Used by URLParamsHandler to parse and validate query parameters
   */
  /**
   * URL parameter schema for deep linking.
   *
   * - `tab`: which sub-tab is active.
   * - `path`: path slug (e.g. 'guide-values-vision'). Detail panel.
   * - `topic`: topic id (e.g. '01.02.01'). Detail panel.
   * - `thread`: per-user thread number (e.g. '5'). Detail panel.
   * - `threadStatus`: status tab a `thread` deep link lives in ('unaddressed'
   *   | 'addressed' | 'dismissed'). Optional sidecar to `thread` only — cross-
   *   surface deep links (e.g. the sidebar Starred section) set it so an
   *   addressed/archived starred thread resolves under its actual tab instead
   *   of silently failing under the Open tab's default load.
   *
   * `path`, `topic`, and `thread` are mutually exclusive — only one detail
   * panel is open at a time.
   */
  static urlParamSchema = {
    tab: {
      type: 'enum',
      enum: ['progress', 'paths', 'threads'],
      default: 'progress'
    },
    path: {
      type: 'string',
      default: null
    },
    topic: {
      type: 'string',
      default: null
    },
    thread: {
      type: 'string',
      validate: (v) => /^\d+$/.test(v) && parseInt(v, 10) >= 1
    },
    threadStatus: {
      type: 'enum',
      enum: ['unaddressed', 'addressed', 'dismissed']
    }
  };

  static ORIENTATION_MESSAGES = {
    progress: 'As we explore Discovery together, our understanding grows. The map shows how deeply we\'ve explored each area of your relationship.',
    paths: 'Paths are guided journeys that gather specific information. Paths deal with relationship topics and some produce a guide upon completion.',
    threads: 'Threads are questions that surfaced during sessions but weren\'t fully explored. Revisiting them can deepen our understanding and open up new insights.',
  };

  /**
   * @param {Object} elements - DOM element references
   * @param {HTMLElement} elements.container - Section container element
   * @param {EventBus} eventBus - Event bus for module communication
   * @param {string|null} coupleId - Current couple ID (null if user not in couple)
   * @param {Object|null} currentUser - Current authenticated or guest user.
   */
  constructor(elements, eventBus, coupleId = null, currentUser = null) {
    this.container = elements.container;
    this.eventBus = eventBus;
    this.coupleId = coupleId;
    this.currentUserData = currentUser;

    // Initialize Discovery API
    this.discoveryAPI = new DiscoveryAPI();

    // State
    this.isActive = false;
    this.currentTab = 'progress'; // 'progress', 'paths', 'threads'

    // BETA PATH GATE — remove after beta; see backend/src/config/betaPathGate.js
    // Master gate flag from the /progress/core payload. Drives hiding the
    // "Threads" page-toggle tab. Defaults false (show) until the payload arrives.
    this.betaPathGate = false;

    // Paths tab state
    this.paths = [];
    this.isLoadingPaths = false;
    this.pathsLoadError = null;

    // Questions tab state
    this.openThreads = [];
    this.questionsFilter = 'unaddressed'; // 'unaddressed' (Open) | 'addressed' (Explored) | 'dismissed' (Archived) — matches backend
    this.questionsSortOrder = 'newest';   // 'newest' | 'oldest'
    this.isLoadingQuestions = false;
    this.questionsLoadError = null;
    this.questionsFilterBar = null;

    // Open Threads select-mode / star / archive / delete state
    this.selectMode = false;
    this.selectedThreadIds = new Set();   // plain OpenThread UUIDs (single type)
    this.threadStarredFilter = false;     // client-side "Starred only" filter
    this.threadActionNotice = '';         // bulk-failure notice (renderSourceErrorsNotice analog)
    this._threadActionInFlight = false;   // guards concurrent bulk runs
    /** Thread-row options menu open/close controller (menu is a child of its `.thread-card`). */
    this._threadRowMenu = new RowMenuController({
      container: () => this.container,
      keyAttr: 'data-thread-id',
      rowSelector: '.thread-card',
      hoverClose: true,
    });

    // Right panel detail state
    //   currentSelection drives the active-card highlight in renderers.
    //   { kind: 'page-info' | 'path' | 'topic' | 'thread' | 'guide', id: string } | null.
    //   currentPath / selectedGuide hold the full detail data for kind 'path' / 'guide'.
    this.currentPath = null;
    this.selectedGuide = null;
    this._panelOpen = false;
    /**
     * Reentrancy guard for `_workOnPath` — true while a DISCOVERY entry
     * hand-off is in flight, so a double-click on the panel CTA or the hero
     * pill cannot fire two navigations. Cleared in `deactivate()`, which is
     * exactly when the hand-off completes (the section unmounts for /chat).
     * Mirrors the Progress tab's `workingOnNext`.
     * @type {boolean}
     */
    this._workingOnPath = false;
    this._detailContent = null;
    this.currentSelection = null;

    // Disposer for the contents rail's scroll-spy over an open guide reader.
    // The host owns it so the payload's `onDispose`, a superseding open and
    // `deactivate` all tear down the SAME observer. The content node it was
    // armed over is remembered beside it so a LATE disposer (the panel's
    // cross-fade disposes the outgoing payload 130ms after the next one armed)
    // can only tear down its own spy — see `_disposeContentsSpyFor`.
    this._contentsSpyDisposer = null;
    this._contentsSpyContentEl = null;

    // Sticky header stack (title → segment nav → filter bar)
    this.stickyStack = null;

    // Progress tab state
    this.partnerData = null; // Loaded from API — Paths tab {{PARTNER_NAME}} copy
    this.progressTopics = []; // Populated from GET /api/discovery/progress/core
    /**
     * Overall understanding percent, straight from `/progress/core`'s
     * `totalWeight`-weighted `overall`. Sole source for the hero's band word and
     * the polar chart's center percent — the client no longer re-derives it.
     * @type {number}
     */
    this.progressOverall = 0;
    /** topicId → topic-summary row (`cardBlurb` teasers). @type {Map<string, Object>} */
    this.topicBlurbs = new Map();
    /** Display-only guided recommendation from `guided/next?peek=1`. */
    this.recommendedNext = null;
    /** Mounted polar chart instance (null when unmounted or mount failed). */
    this.polarChart = null;
    /** Mounted Paths-tab orbit instance (null when unmounted or mount failed). */
    this.pathOrbit = null;
    this.isLoadingProgress = false;
    this.progressLoadError = null;

    this.eventBus?.on?.('badge:state-changed', () => {
      if (!this.isActive) return;
      // render() replaces container.innerHTML, which would drop the orbit's
      // mount node out from under a live instance — release it first, exactly
      // as handleTabChange does for the polar chart. render() rebinds the
      // current tab itself.
      this._destroyPathOrbit();
      this.render();
    });

    this.initialize();
  }

  /**
   * Initialize section
   */
  initialize() {
    eventBus.on('right-panel:closed', (data) => {
      const contentType = typeof data?.contentType === 'string' ? data.contentType : '';
      const isDiscoveryContent = contentType.startsWith('discovery-');
      // The guide panel this section opens carries 'library-guide' (the shared
      // literal), which the discovery- guard below deliberately ignores — so
      // the contents-rail spy is torn down for either before that return.
      if (isDiscoveryContent || contentType === 'library-guide') this._disposeContentsSpy();
      if (!isDiscoveryContent || !this.isActive) return;

      this._clearDetailSelection();
      this._panelOpen = false;
      this._refreshActiveStates();

      // Clean up URL detail params (path / topic / thread). Skipped when
      // isActive is false (cross-section nav owns its own URL change).
      const url = new URL(window.location.href);
      const hadDetailParam = url.searchParams.has('path')
        || url.searchParams.has('topic')
        || url.searchParams.has('thread');
      if (hadDetailParam) {
        url.searchParams.delete('path');
        url.searchParams.delete('topic');
        url.searchParams.delete('thread');
        url.searchParams.delete('threadStatus');
        page.replace(url.pathname + (url.search ? url.search : ''));
      }
    });

    // Universal cross-surface sync bridge (contentSync). Discovery holds only
    // `thread` items (`openThreads`), whose star/archive fields are TIMESTAMPS:
    // `starredAt` for star, `dismissedAt` for the user-archive toggle (NOT
    // `archivedAt`, which is the system quiet-expiry field). Coerce
    // boolean↔timestamp. DOM re-render only while active; the `_panelOpen`
    // guard keeps an inactive section from closing a foreign panel.
    eventBus.on(CONTENT_STARRED, ({ itemType, itemId, starred }) => {
      if (itemType !== 'thread') return;
      if (this._panelOpen && this._isSelected('thread', itemId)) {
        eventBus.emit('right-panel:set-star', { starred });
      }
      const t = this.openThreads.find((th) => th.id === itemId);
      if (!t || Boolean(t.starredAt) === starred) return;
      t.starredAt = starred ? new Date().toISOString() : null;
      if (this.isActive) this._refreshQuestionsList();
    });

    eventBus.on(CONTENT_ARCHIVED, ({ itemType, itemId, archived }) => {
      if (itemType !== 'thread') return;
      const t = this.openThreads.find((th) => th.id === itemId);
      if (!t || Boolean(t.dismissedAt) === archived) return;
      t.dismissedAt = archived ? new Date().toISOString() : null;
      // A toggled thread that no longer belongs in the current tab leaves it;
      // close the panel if the open thread was the one that left.
      this.openThreads = this.openThreads.filter((th) => this.threadMatchesFilter(th));
      if (this._panelOpen && this._isSelected('thread', itemId)
          && !this.openThreads.some((th) => th.id === itemId)) {
        eventBus.emit('right-panel:close');
      }
      if (this.isActive) this._refreshQuestionsList();
    });

    eventBus.on(CONTENT_DELETED, ({ itemType, itemId }) => {
      if (itemType !== 'thread') return;
      const before = this.openThreads.length;
      this.openThreads = this.openThreads.filter((th) => th.id !== itemId);
      // A self-originated delete removed the thread from `openThreads` BEFORE
      // broadcasting, so it reaches here with nothing left to remove — bail so
      // it doesn't emit a redundant second `right-panel:close` alongside the
      // mutation path's own direct emit. A foreign delete always finds the
      // thread (the panel can only be open for a thread in `openThreads`).
      if (this.openThreads.length === before) return;
      if (this._panelOpen && this._isSelected('thread', itemId)) {
        eventBus.emit('right-panel:close');
      }
      if (this.isActive) this._refreshQuestionsList();
    });
  }

  /**
   * True if `currentSelection` matches the given kind + id.
   * @param {'page-info'|'path'|'topic'|'thread'|'guide'} kind
   * @param {string} id
   * @returns {boolean}
   */
  _isSelected(kind, id) {
    return this.currentSelection?.kind === kind && this.currentSelection?.id === id;
  }

  /**
   * Reflect the current selection's active highlight across whatever card
   * list is visible. Each branch checks for its own mount points and is a
   * no-op if that list isn't currently in the DOM.
   */
  _refreshActiveStates() {
    this._refreshPathRowActive();
    this._refreshQuestionsList();
    this._refreshTopicRowActive();
  }

  /**
   * Move the Paths-tab active highlight in place, without re-rendering — the
   * exact counterpart of `_refreshTopicRowActive`, and for the same reason.
   * This runs on EVERY panel transition, and `_refreshPathsList` is an
   * innerHTML replacement: routing through it would wipe the orbit's mount node
   * without `destroy()`, leak its listeners and leave `spotlight()` pointed at
   * detached DOM. `_refreshPathsList` is now reserved for genuine data reloads.
   * No-op when the tab isn't mounted.
   * @private
   */
  _refreshPathRowActive() {
    const list = this.container?.querySelector?.('.discovery-path-list');
    if (!list) return;
    const activeId = this.currentSelection?.kind === 'path'
      ? this.currentSelection.id
      : null;
    list.querySelectorAll(PATH_ROW_BINDING.rowSelector).forEach(row => {
      const isOpenInPanel = row.dataset.pathId === activeId;
      row.classList.toggle('session-card--active', isOpenInPanel);
      // In-place patch (no re-render), so the disclosure state has to be
      // written here too or it goes stale.
      row.setAttribute('aria-expanded', String(isOpenInPanel));
    });
  }

  /**
   * Move the Progress-tab active highlight in place, without re-rendering.
   * Toggling `session-card--active` (rather than swapping the list's innerHTML)
   * keeps the delegated row listeners and the mounted polar chart intact —
   * re-rendering here was what left the Progress tab dead after the first topic
   * detail opened. No-op when the tab isn't mounted.
   */
  _refreshTopicRowActive() {
    const list = this.container?.querySelector?.('.discovery-topic-list');
    if (!list) return;
    const activeId = this.currentSelection?.kind === 'topic'
      ? this.currentSelection.id
      : null;
    list.querySelectorAll('.discovery-topic-row[data-topic-id]').forEach(row => {
      const isOpenInPanel = row.dataset.topicId === activeId;
      row.classList.toggle('session-card--active', isOpenInPanel);
      // This path patches the DOM in place (no re-render), so the disclosure
      // state has to be written here too or it goes stale.
      row.setAttribute('aria-expanded', String(isOpenInPanel));
    });
  }

  /**
   * Forget which detail is open: the selection identity, the path/guide
   * payloads and the detached panel content. Does NOT touch `_panelOpen` or
   * the URL — callers own those. Shared by the panel-closed handler,
   * `clearActiveDetailSelection` and `deactivate()`, so a highlight can never
   * outlive its panel across a page change.
   * @private
   */
  _clearDetailSelection() {
    this.currentPath = null;
    this.selectedGuide = null;
    this.currentSelection = null;
    this._detailContent = null;
  }

  /**
   * Arm the contents rail's scroll-spy over a mounted guide body, superseding
   * whatever was armed before. Armed after each guide emit and again after the
   * post-fetch body replacement, which builds the rail's real rows.
   * @param {Element|null} contentEl - The panel content node holding the rail
   * @private
   */
  _armContentsSpy(contentEl) {
    this._disposeContentsSpy();
    this._contentsSpyContentEl = contentEl;
    this._contentsSpyDisposer = armGuideContentsSpy(contentEl);
  }

  /**
   * Dispose the contents-rail spy ONLY if it is still the one armed over
   * `contentEl` — the payload `onDispose` path.
   *
   * `RightPanelManager` disposes the OUTGOING payload inside its cross-fade,
   * 130ms after the incoming reader's spy was armed; an unconditional dispose
   * there would kill the spy of the guide now on screen.
   *
   * @param {Element|null} contentEl - The content node the caller owns
   * @private
   */
  _disposeContentsSpyFor(contentEl) {
    if (!contentEl || contentEl !== this._contentsSpyContentEl) return;
    this._disposeContentsSpy();
  }

  /**
   * Tear down the contents-rail spy, if one is armed. Idempotent.
   * @private
   */
  _disposeContentsSpy() {
    this._contentsSpyDisposer?.();
    this._contentsSpyDisposer = null;
    this._contentsSpyContentEl = null;
  }

  /**
   * Clear the routed discovery detail selection while leaving the panel open.
   * Used when a non-routed page-info panel replaces a discovery detail.
   */
  clearActiveDetailSelection() {
    this._clearDetailSelection();
    this._refreshActiveStates();

    const url = new URL(window.location.href);
    const hadDetailParam = url.searchParams.has('path')
      || url.searchParams.has('topic')
      || url.searchParams.has('thread');
    if (hadDetailParam) {
      url.searchParams.delete('path');
      url.searchParams.delete('topic');
      url.searchParams.delete('thread');
      url.searchParams.delete('threadStatus');
      page.replace(url.pathname + (url.search ? url.search : ''));
    }
  }

  /**
   * Activate this section (when panel opens to this section).
   *
   * Idempotent self-guard: route handlers re-fire on URL changes (tab
   * clicks, card clicks, panel close), so skip the data load + render on
   * intra-section navigations. applyURLParams still runs after this returns
   * and drives any tab/detail changes.
   */
  async activate() {
    if (this.isActive) return;
    this.isActive = true;
    this._mountToolbarAction();
    await this.loadData();
    if (!this.isActive) return;
    this.render();
  }

  /**
   * Deactivate this section (when switching to another section)
   */
  deactivate() {
    this.isActive = false;
    this._unmountToolbarAction();
    this._disposeContentsSpy();

    // The path entry hand-off ends here (the section unmounts for /chat), so
    // release its reentrancy guard rather than latching it for the session.
    this._workingOnPath = false;

    // Destroy the hero visuals (frees the chart's SVG + wedge listeners and
    // the orbit's disc listeners)
    this._destroyPolarChart();
    this._destroyPathOrbit();

    // Clean up the sticky header stack
    this.stickyStack?.destroy();
    this.stickyStack = null;

    // Clean up orientation banner
    if (this.orientationBanner) {
      this.orientationBanner.destroy();
      this.orientationBanner = null;
    }

    // Remove thread-row action delegation + close any open row-kebab menu
    // (and its own document-level outside-click listener)
    this.container.removeEventListener('click', this.handleThreadActionClick);
    this.container.removeEventListener('contextmenu', this.handleThreadContextMenu);
    this._threadRowMenu.close();

    // Clean up questions filter bar
    if (this.questionsFilterBar) {
      this.questionsFilterBar.destroy();
      this.questionsFilterBar = null;
    }
    if (this._panelOpen) {
      eventBus.emit('right-panel:close');
      this._panelOpen = false;
    }
    // The cross-page close's `right-panel:closed` lands after `isActive` is
    // already false, so the closed handler (rightly) ignores it and never
    // clears the selection. Clear it here, or the next `render()` on return
    // paints the old row active with no panel behind it.
    this._clearDetailSelection();
  }

  /**
   * Load data from backend
   */
  async loadData() {
    const promises = [this.loadProgressData()];
    if (this.coupleId) {
      promises.push(this.loadPartnerData());
    }
    await Promise.all(promises);
  }

  /**
   * Load everything the Progress tab renders, in one parallel `allSettled`
   * round: the shared topic-progress model (core progress + teaser blurbs,
   * `core/progressData.js` — shared with the understanding-map panel) and a
   * read-only peek at the guided recommendation, which is this tab's alone.
   *
   * `allSettled`, never `Promise.all`: the two have different failure
   * consequences. Only the progress model sets the page error state; a failed
   * peek simply hides the recommended destination.
   */
  async loadProgressData() {
    this.isLoadingProgress = true;
    this.progressLoadError = null;
    try {
      const [progressResult, peekResult] = await Promise.allSettled([
        loadTopicProgress({ discoveryAPI: this.discoveryAPI }),
        this.discoveryAPI.peekNextForGuided()
      ]);

      // Recommended-next peek — display-only payload; a failure hides the destination.
      this.recommendedNext = peekResult.status === 'fulfilled'
        ? (peekResult.value?.data || null)
        : null;

      // Core progress — the only fetch whose failure is a page error.
      if (progressResult.status === 'rejected') throw progressResult.reason;
      const { topics, overall, betaPathGate, blurbs } = progressResult.value;
      this.topicBlurbs = blurbs;
      // BETA PATH GATE — remove after beta; see backend/src/config/betaPathGate.js
      this.betaPathGate = betaPathGate;
      this.progressOverall = overall;
      this.progressTopics = topics;
    } catch (error) {
      console.error('[DiscoverySection] Error loading progress data:', error);
      this.progressTopics = [];
      this.progressLoadError = 'Unable to load progress. Please try again.';
    } finally {
      this.isLoadingProgress = false;
    }
  }

  /**
   * Load partner information from API
   */
  async loadPartnerData() {
    try {
      const result = await this.discoveryAPI.getPartner();
      if (result?.data) {
        this.partnerData = {
          firstName: result.data.firstName || null,
          profilePictureUrl: result.data.profilePictureUrl || null
        };
      } else {
        this.partnerData = null;
      }

    } catch (error) {
      console.error('[DiscoverySection] Error loading partner data:', error);
      this.partnerData = null;
    }
  }

  /**
   * Render complete interface
   */
  render() {
    this.container.innerHTML = `
      <div class="explore-section">
        <div class="explore-container explore-container--fixed-action">
          <div class="explore-view--list" data-sticky-stack>
            ${renderPageInfoTitle('Discovery')}

            <div class="page-header content-width">
              <div class="page-header__subtitle-row">
                <p class="page-header__subtitle">Building understanding together.</p>
              </div>
            </div>

            ${this._renderNavBar()}

            <div class="share-content content-width">
              ${this.renderTabContent()}
            </div>
          </div>
          ${this.renderFixedAction()}
        </div>
      </div>
    `;

    this._showOrientationBanner(this.currentTab);

    this.attachEventListeners();
    this._initStickyStack();
  }

  /**
   * The fixed bottom "Begin discovery" action — mobile-only (<1020px; desktop
   * shows the toolbar action instead, see _mountToolbarAction). Anchored to
   * the shared .explore-container--fixed-action frame as a sibling of the
   * scroll container, so it stays put while the page scrolls beneath it.
   * @returns {string} HTML string
   */
  renderFixedAction() {
    return `
      <div class="page-fixed-action" data-fixed-page-action>
        <div class="page-fixed-action__inner">
          <button class="btn btn--primary page-fixed-action__button" data-action="begin-discovery" type="button">Begin discovery</button>
        </div>
      </div>
    `;
  }

  /**
   * Mount the desktop "Begin discovery" primary action into the page toolbar's
   * right slot (flush left of the X close button; hidden <1020px by CSS, where
   * renderFixedAction's bottom bar takes over). Restores the affordance of the
   * retired header CTA: parameterless guided entry via handleBeginSharing(),
   * independent of the Progress tab's Up-next pill. The slot lives outside
   * `container`, so it survives tab re-renders and is mounted once per
   * activation; PageManager.clearToolbarActions() also wipes it on every page
   * change.
   */
  _mountToolbarAction() {
    const slot = document.getElementById('page-toolbar-header-right-actions');
    if (!slot) return;
    slot.innerHTML = '';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn--primary btn--small btn--toolbar';
    btn.textContent = 'Begin discovery';
    btn.addEventListener('click', () => this.handleBeginSharing());
    slot.appendChild(btn);
  }

  /** Remove the toolbar action mounted by _mountToolbarAction(). Idempotent. */
  _unmountToolbarAction() {
    const slot = document.getElementById('page-toolbar-header-right-actions');
    if (slot) slot.innerHTML = '';
  }

  /**
   * Render the page-toggle tab bar under the page title.
   * @returns {string} HTML string
   */
  _renderNavBar() {
    const items = `
      ${this.renderNavItem('progress', 'Progress')}
      ${this.renderNavItem('paths', 'Paths')}
      ${/* BETA PATH GATE — hide the Threads tab pill while the gate is on; the
          tab stays reachable via ?tab=threads. Remove after beta. */''}
      ${this.renderNavItem('threads', 'Threads', null, this.betaPathGate)}
    `;
    return renderSegmentNavBar(items, { ariaLabel: 'Content filter' });
  }

  /**
   * Render a single nav item for the tab bar
   * @param {string} id - Tab identifier
   * @param {string} label - Display label
   * @param {number} [count] - Optional item count
   * @param {boolean} [hidden] - Whether to hide the nav item (display:none)
   * @returns {string} HTML string
   */
  renderNavItem(id, label, count = null, hidden = false) {
    const isActive = this.currentTab === id;
    const indicator = window.badgeService?.getTabIndicator('discovery', id);
    return renderSegmentNavItem({ id, label, count, hidden, active: isActive, indicator });
  }

  /**
   * Render content based on current tab
   * @returns {string} HTML string
   */
  renderTabContent() {
    switch (this.currentTab) {
      case 'progress':
        return this.renderProgressContent();
      case 'paths':
        return this.renderDiscoveryContent();
      case 'threads':
        return this.renderQuestionsContent();
      default:
        return '<p>Select a tab to view content.</p>';
    }
  }

  // ===== PROGRESS TAB HELPER METHODS =====

  /**
   * Convert a 0-100 progress value to visual bar width.
   * 0% maps to 5% width so every bar shows a sliver of progress.
   * Still used by the Paths tab (`_buildPathRowModel`, `showDetailView`).
   * @param {number} progress - Progress value 0-100
   * @returns {number} Visual width percentage 5-100
   */
  progressToBarWidth(progress) {
    return 5 + (progress / 100) * 95;
  }

  /**
   * The topics in chart/row display order, shaped for the polar chart.
   * `label` feeds the chart's hover tooltip.
   * @returns {Array<{id: string, progress: number, label: string}>}
   * @private
   */
  _chartTopics() {
    return this.progressTopics.map(t => ({
      id: t.id,
      progress: t.progress,
      label: t.label
    }));
  }

  /**
   * Derive the Up-next pill's routing + label from the display-only
   * `guided/next?peek=1` payload. The pill's label IS the destination — button
   * and action are one thing — so pressing it is always a specific move. The
   * estimate rides on the eyebrow above rather than inside the pill: most
   * section names are too long to share a line with it at the aside's width.
   *
   * Returns null when there is nothing specific to recommend (journey
   * complete, or the peek fetch failed). The pill still renders in that case,
   * reading "Continue Discovery" with no eyebrow, and falls back to the
   * parameterless guided entry — the Progress tab never loses its discovery
   * entry point.
   *
   * `minutes` is null for Foundation, for an L3 candidate (open-ended by
   * design), and whenever the backend could not resolve the next section. It
   * is never re-derived client-side.
   *
   * @returns {{action: 'foundation'|'topic'|'l3', topicId?: string, ctaLabel: string, minutes: number|null}|null}
   * @private
   */
  _recommendedNextStep() {
    const rec = this.recommendedNext;
    if (!rec) return null;

    if (rec.type === 'foundation_required') {
      return { action: 'foundation', ctaLabel: 'Starting Your Journey', minutes: null };
    }
    if (rec.type === 'l3_session') {
      const name = rec.sectionName;
      return {
        action: 'l3',
        ctaLabel: name ? `Go deeper on ${name}` : 'Go deeper',
        minutes: null
      };
    }
    if (rec.journeyComplete || !rec.topicId) return null;

    // `nextSection` is the orchestrator's own read of what this topic serves
    // next (peek-only, null on any failure). Without it the pill names the
    // topic and drops the estimate rather than guessing at either.
    const group = this.progressTopics.find(t => t.id === rec.topicId) || null;
    const next = rec.nextSection || null;
    return {
      action: 'topic',
      topicId: rec.topicId,
      ctaLabel: next?.sectionName || rec.label || group?.label || 'Recommended topic',
      minutes: next?.estimatedMinutes ?? null
    };
  }

  /**
   * The hero aside: where you are (band word + adaptive stat line, from the
   * shared renderer) over the Up-next block this tab alone carries.
   * @returns {string} HTML string
   * @private
   */
  _renderProgressAside() {
    // The Up-next block carries the page's ONE primary action. The pill's own
    // label names the destination, so there is no separate headline to keep in
    // sync with it; the eyebrow carries the "next" framing plus the estimate.
    // The understanding-map panel renders the state block without this one
    // (progressHero).
    // Section display names run long, so the label truncates to one line and
    // `data-tooltip` carries the full name on hover (wired in
    // bindProgressEvents, which only shows it when the text is actually
    // clipped). No aria-label without a step: the generic fallback label IS
    // the visible text, so naming it again would only stutter.
    const step = this._recommendedNextStep();
    const ctaLabel = step ? step.ctaLabel : 'Continue Discovery';
    const eyebrow = step
      ? `Up next${step.minutes ? ` · ~${step.minutes} min` : ''}`
      : '';
    const aria = step ? ` aria-label="Continue discovery: ${this.escapeHtml(ctaLabel)}"` : '';
    const nextHtml = `
      <div class="hero-next">
        ${step ? `<span class="hero-next__eyebrow">${this.escapeHtml(eyebrow)}</span>` : ''}
        <button type="button" class="btn btn--secondary hero-next__cta" data-action="recommended-next"
                data-tooltip="${this.escapeHtml(ctaLabel)}" data-tooltip-position="above"${aria}>
          <span class="hero-next__cta-text">${this.escapeHtml(ctaLabel)}</span>
        </button>
      </div>
    `;

    return renderProgressAside(this.progressTopics, this.progressOverall, { nextHtml });
  }


  /**
   * Render the Progress tab: one white rounded card holding the hero row
   * (polar map beside the state + Up-next aside, stacking at narrow widths) and
   * the topic rows; the segment nav stays outside on the page background.
   * @returns {string} HTML string
   */
  renderProgressContent() {
    // Loading / error / empty states render the heading plus the empty state
    // alone — no KPI strip and no chart, which would both be meaningless.
    let emptyStateHtml = null;
    if (this.isLoadingProgress) {
      emptyStateHtml = renderEmptyState({ title: 'Loading progress…' });
    } else if (this.progressLoadError) {
      emptyStateHtml = renderEmptyState({ title: this.progressLoadError });
    } else if (this.progressTopics.length === 0) {
      emptyStateHtml = renderEmptyState({
        title: 'No topics yet',
        description: 'Complete a discovery session to start building your profile.'
      });
    }

    if (emptyStateHtml) {
      return `
        <div class="share-tab-content" data-tab-content="progress">
          ${emptyStateHtml}
        </div>
      `;
    }

    const activeTopicId = this.currentSelection?.kind === 'topic' ? this.currentSelection.id : null;
    const rowsHtml = this.progressTopics
      .map(topic => renderTopicRow(topic, this.topicBlurbs, { activeTopicId }))
      .join('');

    return `
      <div class="share-tab-content" data-tab-content="progress">
        <div class="discovery-progress-card">
          <div class="discovery-progress-hero">
            ${this._renderProgressAside()}
            <div class="discovery-polar" data-polar-chart></div>
          </div>
          <div class="session-cards-container discovery-topic-list">
            ${rowsHtml}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Bind event listeners for progress tab. Runs on every progress render, so
   * everything here is either delegated from a freshly-rendered node or torn
   * down first.
   */
  bindProgressEvents() {
    const tab = this.container?.querySelector?.('[data-tab-content="progress"]');
    if (!tab) return;

    // Polar chart. Fail-soft: a construction error must still leave the KPI
    // strip and the rows fully usable.
    this._destroyPolarChart();
    const chartMount = tab.querySelector('[data-polar-chart]');
    if (chartMount) {
      try {
        this.polarChart = new PolarProgressChart(chartMount, {
          topics: this._chartTopics(),
          centerPct: this.progressOverall,
          onWedgeHover: (topicId) => this._setTopicRowHot(topicId),
          onWedgeClick: (topicId) => this._navigateToTopic(topicId)
        });
      } catch (err) {
        console.warn('[DiscoverySection] polar chart mount failed:', err);
        this.polarChart = null;
      }
    }

    // Topic rows — the shared hero binder (delegated from the stable list
    // container so the handlers survive in-place active-highlight updates).
    // Opening pushes URL; the route handler picks it up via applyURLParams.
    const list = tab.querySelector('.discovery-topic-list');
    if (list) {
      bindTopicRows(list, {
        onOpen: (topicId) => this._navigateToTopic(topicId),
        getChart: () => this.polarChart
      });
    }

    // Up-next CTA. The peeked payload is display-only and is never
    // threaded into a session start — each branch re-enters through the real
    // (non-peek) entry path.
    const nextTile = tab.querySelector('[data-action="recommended-next"]');
    if (nextTile) {
      // Full section name on hover, but only when the pill actually clipped it
      // — a tooltip that repeats text already fully on screen is just noise.
      // The node is rebuilt on every progress render, so its listeners go with
      // it; no teardown to track (same contract as bindTopicRows above).
      const ctaText = nextTile.querySelector('.hero-next__cta-text');
      Tooltip.setup(nextTile, {
        position: 'above',
        shouldShow: () => !!ctaText && ctaText.scrollWidth > ctaText.clientWidth
      });

      // Guards against double-fire while the entry hand-off is in flight.
      let workingOnNext = false;
      nextTile.addEventListener('click', async () => {
        if (workingOnNext) return;
        workingOnNext = true;
        const step = this._recommendedNextStep();
        if (!step) {
          // No specific recommendation (journey complete / peek failed):
          // plain guided entry, same as the toolbar's "Begin discovery".
          this.handleBeginSharing();
          return;
        }
        if (step.action === 'foundation') {
          // No pathMetadata: _enterFoundation self-fetches via
          // getPathById('foundation') and overwrites caller metadata anyway.
          eventBus.emit('navigate', { page: 'chat', mode: 'DISCOVERY', pathId: 'foundation' });
        } else if (step.action === 'l3') {
          // Parameterless guided entry re-runs the non-peek fetchNextForGuided(),
          // which hands _startL3Session a fully prepared config.
          eventBus.emit('navigate', { page: 'chat', mode: 'DISCOVERY' });
        } else {
          // Shared two-step (persist selection → navigate DISCOVERY).
          await enterTopicDiscovery(step.topicId, { discoveryAPI: this.discoveryAPI });
        }
      });
    }
  }

  /**
   * Push `?topic=N` for a topic row. Push when the panel is closed; replace
   * when it's open (cross-fade swap, keeps history flat). The route handler
   * resolves the param via applyURLParams. Mirrors
   * `_navigateToThreadByQuestionId`.
   * @param {string} topicId
   * @private
   */
  _navigateToTopic(topicId) {
    if (!topicId) return;
    // Toggle: re-activating the row already open dismisses the panel, same as
    // its × (the `right-panel:closed` handler owns the URL scrub). Doubles as
    // the dedup guard — page.js doesn't dedup identical navigations.
    if (this._panelOpen && this._isSelected('topic', topicId)) {
      eventBus.emit('right-panel:close');
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('topic', topicId);
    url.searchParams.delete('path');
    const target = url.pathname + url.search;
    if (this._panelOpen) {
      page.replace(target);
    } else {
      page(target);
    }
  }

  /**
   * Tear down the mounted polar chart, if any. Safe to call repeatedly.
   * @private
   */
  _destroyPolarChart() {
    if (this.polarChart) {
      try { this.polarChart.destroy(); } catch (e) { /* noop */ }
      this.polarChart = null;
    }
  }

  /**
   * Push `?path=<id>` for a path row, the orbit's discs and the hero pill —
   * the Paths-tab twin of `_navigateToTopic`. Push when the panel is closed;
   * replace when it's open (cross-fade swap, keeps history flat).
   * @param {string} pathId
   * @private
   */
  _navigateToPath(pathId) {
    if (!pathId) return;

    // No access gate here. Routing is display-only for every KNOWN path —
    // withheld and phase-locked ones open the same drawer with no work-entry
    // button (`showDetailView`), and access is enforced solely on that action
    // (`_workOnPath`). `applyURLParams` scrubs only ids it cannot resolve.

    // Toggle: re-activating the row already open dismisses the panel, same as
    // its × (the `right-panel:closed` handler owns the URL scrub). Doubles as
    // the dedup guard — page.js doesn't dedup identical navigations.
    if (this._panelOpen && this._isSelected('path', pathId)) {
      eventBus.emit('right-panel:close');
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('path', pathId);
    url.searchParams.delete('topic');
    const target = url.pathname + url.search;
    if (this._panelOpen) {
      page.replace(target);
    } else {
      page(target);
    }
  }

  /**
   * Tear down the mounted path orbit, if any. Safe to call repeatedly.
   * Mirrors `_destroyPolarChart`.
   * @private
   */
  _destroyPathOrbit() {
    if (this.pathOrbit) {
      try { this.pathOrbit.destroy(); } catch (e) { /* noop */ }
      this.pathOrbit = null;
    }
  }

  /**
   * Reflect the orbit's hovered disc onto the path rows below (pass null to
   * clear). Driven by `PathOrbit`'s `onHover`, which never fires from
   * `spotlight()` — so this can't echo back into the row-hover channel.
   * @param {string|null} pathId
   * @private
   */
  _setPathRowHot(pathId) {
    const list = this.container?.querySelector?.('.discovery-path-list');
    if (!list) return;
    setTopicRowHot(list, pathId, PATH_ROW_BINDING);
  }

  /**
   * Reflect the chart's hovered wedge onto the topic rows below: paint the row
   * whose topic matches (pass null to clear). Driven by the chart's
   * `onWedgeHover`, which only fires for pointer-originated wedge hover — so
   * this never echoes back into `chart.spotlight()` from the row hover path.
   * @param {string|null} topicId
   * @private
   */
  _setTopicRowHot(topicId) {
    const list = this.container?.querySelector?.('.discovery-topic-list');
    if (!list) return;
    setTopicRowHot(list, topicId);
  }

  /**
   * Render the Paths tab: the Progress tab's own chassis with different data —
   * one white rounded card holding the hero row (path orbit beside the state +
   * Up-next aside, stacking at narrow widths) over hairline-separated path
   * rows. The segment nav stays outside on the page background.
   *
   * `data-paths-list` rides the row container itself: `attachPathsEventListeners`
   * delegates from it and `_refreshPathsList` replaces its contents, so the two
   * must address the same node.
   *
   * Loading / error / no-paths states render the empty state alone — a hero
   * describing a journey we have no data for would be meaningless.
   *
   * @returns {string} HTML string
   */
  renderDiscoveryContent() {
    const hasPaths = !this.isLoadingPaths && !this.pathsLoadError && this.paths.length > 0;
    if (!hasPaths) {
      return `
        <div class="share-tab-content" data-tab-content="paths">
          ${this.renderPathsList()}
        </div>
      `;
    }

    return `
      <div class="share-tab-content" data-tab-content="paths">
        <div class="discovery-progress-card">
          <div class="discovery-progress-hero">
            ${this._renderPathsAside()}
            <div class="discovery-orbit" data-path-orbit></div>
          </div>
          <div class="session-cards-container discovery-path-list" data-paths-list>
            ${this.renderPathsList()}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * The path the header speaks for, chosen deterministically: among paths that
   * are workable and unfinished, the first one **in the order the rows render
   * in** — preferring one already in progress. Ties cannot occur because that
   * order is total, which matters because several paths can be in progress at
   * once and withheld paths can carry incidental progress from guided
   * discovery.
   *
   * @returns {Object|null} Path listing entry, or null when everything
   *   available is finished
   * @private
   */
  _currentHeaderPath() {
    const candidates = this._flatOrderedPaths()
      .filter(p => this._isPathWorkable(p) && !p.isCompleted);
    return candidates.find(p => p.isInProgress) || candidates[0] || null;
  }

  /**
   * The accumulating stat line: how much has been finished, never a fraction.
   * Today's denominator is a beta slice, so "1 of 7" would silently regress to
   * "1 of 12" as more paths ship. A zero clause is omitted rather than printed.
   *
   * @returns {string} e.g. `1 path complete · 1 guide earned`, or ''
   * @private
   */
  _pathsStatLine() {
    const paths = this.paths || [];
    const complete = paths.filter(p => p.isCompleted).length;
    const guides = paths.filter(
      p => EARNED_GUIDE_STATUSES.includes(p.guideStatus?.status)
    ).length;

    const clauses = [];
    if (complete > 0) clauses.push(`${complete} path${complete === 1 ? '' : 's'} complete`);
    if (guides > 0) clauses.push(`${guides} guide${guides === 1 ? '' : 's'} earned`);
    return clauses.join(' · ');
  }

  /**
   * The Paths hero aside: which path you're on (or should start next), that
   * path's own facts, and a verb pill. Every slot describes the one path the
   * band names — an in-flight path shows its bar and what is left, an unstarted
   * one shows what it is — so the journey-wide count (`_pathsStatLine`) appears
   * only in the all-complete state, where the band IS the journey. The band
   * names the path, so the pill never does — it carries the action alone
   * (`Continue` / `Begin`). When every available path is finished there is no
   * next step, so eyebrow, Up-next block and pill all drop.
   *
   * @returns {string} HTML string
   * @private
   */
  _renderPathsAside() {
    const current = this._currentHeaderPath();

    if (!current) {
      return renderHeroAside({
        band: 'All paths complete',
        stat: this._pathsStatLine()
      });
    }

    const inProgress = Boolean(current.isInProgress);
    const eyebrow = inProgress ? 'Current path' : 'Start next';
    const ctaLabel = inProgress ? 'Continue' : 'Begin';

    let stateHtml = '';
    let nextEyebrow = '';
    if (inProgress) {
      // The rows' own bar component, so the hero and the row below it draw the
      // same object for the same fact.
      const percent = this.resolvePathProgress(current).currentUser.percentComplete || 0;
      const remaining = this._pathRemainingPhrase(current, { separator: ' · ', minutePrefix: 'about ' });
      stateHtml = `
        <div class="hero-state__facts">
          <div class="understanding__bar-track" style="--bar-width:${this.progressToBarWidth(percent)}%"><div class="understanding__bar-fill"></div></div>
          <span class="hero-state__facts-line"><b>${percent}%</b>${remaining ? ` · ${this.escapeHtml(remaining)}` : ''}</span>
        </div>
      `;
    } else {
      const description = this._pathDescription(current);
      if (description) stateHtml = `<p class="hero-state__desc">${this.escapeHtml(description)}</p>`;
      if (current.estimatedDuration > 0) nextEyebrow = `~${this.formatDuration(current.estimatedDuration)}`;
    }

    const nextHtml = `
      <div class="hero-next">
        ${nextEyebrow ? `<span class="hero-next__eyebrow">${this.escapeHtml(nextEyebrow)}</span>` : ''}
        <button type="button" class="btn btn--secondary hero-next__cta" data-action="paths-next"
                data-path-id="${this.escapeHtml(current.id)}"
                aria-label="${this.escapeHtml(`${ctaLabel}: ${current.name}`)}">
          <span class="hero-next__cta-text">${this.escapeHtml(ctaLabel)}</span>
        </button>
      </div>
    `;

    return renderHeroAside({ eyebrow, band: current.name, stateHtml, nextHtml });
  }

  /**
   * The "how much is left" phrase shared by the hero eyebrow and the
   * in-progress row summary. `sectionsRemaining` is the backend's own count of
   * sections not fully resolved (questionnaire + conversation); the
   * questionnaire subtraction is the fallback for payloads from before the
   * increment model. `minutesRemaining` is work left, not `estimatedDuration`
   * (the path total).
   *
   * @param {Object} path - Path listing entry
   * @param {Object} [opts]
   * @param {string} [opts.separator=', '] - Joiner between the two clauses
   * @param {string} [opts.minutePrefix=''] - Prefix on the minutes clause
   * @param {string} [opts.minuteWord='min'] - Unit word for the minutes clause
   * @returns {string} e.g. `4 sections left · ~50 min`, or '' when nothing is left
   * @private
   */
  _pathRemainingPhrase(path, { separator = ', ', minutePrefix = '', minuteWord = 'min' } = {}) {
    const { currentUser } = this.resolvePathProgress(path);
    const sections = Number.isFinite(currentUser.sectionsRemaining)
      ? currentUser.sectionsRemaining
      : Math.max(0, (currentUser.questionnairesTotal || 0) - (currentUser.questionnairesCompleted || 0));
    const minutes = path.minutesRemaining || 0;

    const clauses = [];
    if (sections > 0) clauses.push(`${sections} section${sections === 1 ? '' : 's'} left`);
    if (minutes > 0) clauses.push(`${minutePrefix}${minutes} ${minuteWord}`);
    return clauses.join(separator);
  }

  /**
   * Whether a path can actually be worked today — the complement of the two
   * withheld reasons AND of an unmet phase gate. The backend folds the access
   * resolver into `phaseAccess`, so each reason is checked explicitly rather
   * than inferred from it.
   * @param {Object} path - Path listing entry
   * @returns {boolean}
   * @private
   */
  _isPathWorkable(path) {
    return (path?.phaseAccess ?? false)
      && path?.accessReason !== 'premium_required'
      && path?.accessReason !== 'not_built';
  }

  /**
   * Whether a path's artwork recedes: only when its content does not exist
   * yet. Premium and phase-locked paths keep full colour — they are real
   * content the reader can go and get — and say so with a padlock instead
   * (`_pathBadge`).
   * @param {Object} path - Path listing entry
   * @returns {boolean}
   * @private
   */
  _isPathDimmed(path) {
    return path?.accessReason === 'not_built';
  }

  /**
   * The rim badge an UNFINISHED path's disc wears (a finished one wears the
   * check, decided by the caller): a padlock when the viewer cannot work it
   * yet, whether that is the free-tier allowance or an unmet phase gate. A
   * not-built path gets no badge — nothing the viewer does unlocks it, and
   * the dimmed artwork plus `Coming soon` already say so.
   * @param {Object} path - Path listing entry
   * @returns {'lock'|null}
   * @private
   */
  _pathBadge(path) {
    if (path?.accessReason === 'not_built') return null;
    if (path?.accessReason === 'premium_required') return 'lock';
    return (path?.phaseAccess ?? false) === false ? 'lock' : null;
  }

  /**
   * Full URL of a path's card artwork, or '' when it declares none.
   * @param {Object} path - Path listing entry
   * @returns {string}
   * @private
   */
  _pathImageUrl(path) {
    const image = path?.cardContent?.image;
    return image ? `assets/images/paths/${image}` : '';
  }

  /**
   * Render the paths list region: the ordered path rows, with a quiet in-sheet
   * label opening each withheld group. The ordering itself lives in
   * `_orderedPathGroups`, which the hero's orbit reads from the same call — so
   * the ring order and the row order cannot drift.
   * @returns {string} HTML string
   */
  renderPathsList() {
    if (this.isLoadingPaths) {
      return renderEmptyState({ title: 'Loading paths…' });
    }

    if (this.pathsLoadError) {
      return renderEmptyState({ title: this.pathsLoadError });
    }

    if (this.paths.length === 0) {
      return renderEmptyState({
        title: 'No paths yet',
        description: 'Check back later for new discovery paths.'
      });
    }

    const activePathId = this.currentSelection?.kind === 'path'
      ? this.currentSelection.id
      : null;

    return this._orderedPathGroups()
      .filter(group => group.paths.length > 0)
      .map(group => group.labelHtml + group.paths
        .map(path => renderPathRow(this._buildPathRowModel(path), { activePathId }))
        .join(''))
      .join('');
  }

  /**
   * Every path the tab shows, in render order, flattened out of its groups.
   * The one ordered model the rows, the orbit and the header all read.
   * @returns {Array<Object>} Path listing entries in row order
   * @private
   */
  _flatOrderedPaths() {
    return this._orderedPathGroups().flatMap(group => group.paths);
  }

  /**
   * The tab's ordering, as groups of paths each optionally opened by a label.
   * Two layouts, switched on whether any path is still unbuilt.
   *
   * @returns {Array<{labelHtml: string, paths: Array<Object>}>} Groups in render order
   * @private
   */
  _orderedPathGroups() {
    const all = this.paths || [];

    // ─── PATH ACCESS — unbuilt-path layout. ────────────────────────────────
    // See backend/src/config/pathAccess.js. This block lives only as long as
    // some path is still unbuilt; it disappears with the last `not_built` id.
    //
    // While any path is unbuilt, the Paths tab collapses to a fixed three-group
    // layout that the progress-driven sort below cannot express:
    //
    //   1. Workable paths pin to the top in journey order, ALWAYS — including
    //      once complete. The default sort drops completed cards to the bottom,
    //      which would bury the only paths a user can actually work beneath
    //      nine they cannot.
    //   2. Every withheld path sits below a divider, ALWAYS — including ones
    //      carrying progress or completion (from before the gate landed, or
    //      from guided discovery, which is deliberately not gated). The default
    //      sort hoists anything in-progress into the top bucket, floating
    //      disabled cards above the divider.
    //   3. `premium_required` sits ABOVE `not_built`: it is actionable (the
    //      user can unlock it) while an unbuilt path is not.
    //
    // Ordering inside each group is `phase` then `order` — a fixed journey
    // order rather than a progress-derived one, so cards do not reshuffle
    // under the user as they complete work.
    //
    // The layout is derived from the payload rather than a frontend flag: the
    // backend only sets `accessReason: 'not_built'` while the readiness gate is
    // on.
    const hasUnbuiltPaths = all.some(p => p.accessReason === 'not_built');
    if (hasUnbuiltPaths) {
      const byJourneyOrder = (a, b) =>
        ((a.phase ?? 0) - (b.phase ?? 0)) || ((a.order ?? 0) - (b.order ?? 0));
      // Group 1 is the complement of the two withheld reasons, not
      // `phaseAccess === true`: an ordinary phase-locked path (a built path
      // awaiting Foundation) belongs with the workable set, not below a
      // divider whose copy would be false for it.
      const workable = all
        .filter(m => m.accessReason !== 'premium_required' && m.accessReason !== 'not_built')
        .sort(byJourneyOrder);
      const premiumLocked = all
        .filter(m => m.accessReason === 'premium_required')
        .sort(byJourneyOrder);
      const notBuilt = all
        .filter(m => m.accessReason === 'not_built')
        .sort(byJourneyOrder);

      return [
        { labelHtml: '', paths: workable },
        { labelHtml: this.renderPremiumPathsNotice(), paths: premiumLocked },
        // No label over the not-built run: their `Coming soon` status chips
        // already say it row by row, and a heading restating them only bought
        // another divider (Doug 2026-09-04).
        { labelHtml: '', paths: notBuilt }
      ];
    }
    // ─── end PATH ACCESS ───────────────────────────────────────────────────

    // Order paths canonically — independent of section headers:
    // in-progress (urgency-first) → available accessible → available
    // phase-locked → Premium → coming soon → completed.
    const inProgress = all.filter(m => m.isInProgress);
    const completed = all.filter(m => m.isCompleted);
    const available = all.filter(m => !m.isInProgress && !m.isCompleted);

    const sortedInProgress = [...inProgress].sort((a, b) => {
      const aUrgent = this.isPathUrgent(a);
      const bUrgent = this.isPathUrgent(b);
      if (aUrgent && !bUrgent) return -1;
      if (!aUrgent && bUrgent) return 1;
      return 0;
    });
    const accessible = available.filter(m => (m.phaseAccess ?? false));
    // PATH ACCESS — see backend/src/config/pathAccess.js.
    // Withheld paths also arrive with phaseAccess === false (the backend folds
    // the resolver into it), so each reason must be split out of `phaseLocked`
    // — otherwise they'd sit under "More paths unlock after Starting Your
    // Journey", which is false for both: an unbuilt path never unlocks, and a
    // Premium path unlocks by subscribing, not by finishing Foundation.
    const premiumLocked = available.filter(m => m.accessReason === 'premium_required');
    const notBuilt = available.filter(m => m.accessReason === 'not_built');
    const phaseLocked = available.filter(m => (
      !(m.phaseAccess ?? false)
      && m.accessReason !== 'premium_required'
      && m.accessReason !== 'not_built'
    ));

    return [
      { labelHtml: '', paths: sortedInProgress },
      { labelHtml: '', paths: accessible },
      { labelHtml: this.renderLockedPathsNotice(), paths: phaseLocked },
      // PATH ACCESS — Premium above Coming soon: unlockable before unbuildable.
      { labelHtml: this.renderPremiumPathsNotice(), paths: premiumLocked },
      // Unlabelled — see the unbuilt-path branch above.
      { labelHtml: '', paths: notBuilt },
      { labelHtml: '', paths: completed }
    ];
  }

  /**
   * Build the presentation-ready view model one path row renders from.
   *
   * `renderPathRow` is presentation-only by contract: viewer identity, partner
   * names, completion dates, percent math and disclosure obligations all live
   * here, on the section, and reach it already resolved.
   *
   * **Status precedence — first match wins.** The chip is words only and
   * carries the row's tone (discovery.css §ROW STATUS TONES): green for an
   * outcome, amber for something the viewer should tap, grey for a stage. Two
   * constraints it encodes are load-bearing:
   *
   * - **Both partner-driven states are gated on `partnerCompletionBlocks`**
   *   (see `isPathUrgent`): `Waiting on X` on a finished path, and the amber
   *   `X finished` nudge on an in-flight one. On an individual-only path a
   *   partner's completion blocks nothing and asks nothing, so it is reported
   *   as a plain fact-line clause instead. Without the gate a partner sitting
   *   at 0% behind a pending invite would be labelled as blocking the user on
   *   every individual path.
   * - **Solo users have no partner states.** `getPathsWithUserProgress` emits
   *   `partnerProgress` as a one-element array holding the requesting user, and
   *   `resolvePathProgress` correctly returns `partner: null` — so the partner
   *   rows must fall through cleanly, with no name to interpolate.
   *
   * The disc's state overlays (`ringPercent`, `badge`) are the orbit's own
   * vocabulary (`_buildPathOrbitModel`), so the tile and the disc agree.
   *
   * @param {Object} path - Path listing entry as the backend emits it
   * @returns {{id: string, name: string, imageUrl: string,
   *   status: {kind: string, label: string}|null, ringPercent: number|null,
   *   badge: ('check'|'lock'|null), summary: string, dimmed: boolean,
   *   workable: boolean, badgeHtml: string}} Row view model
   * @private
   */
  _buildPathRowModel(path) {
    const { currentUser, partner } = this.resolvePathProgress(path);
    const isCompleted = Boolean(path.isCompleted);
    const isInProgress = Boolean(path.isInProgress) && !isCompleted;
    const partnerBlocks = Boolean(path.partnerCompletionBlocks);
    const partnerComplete = !!partner && this.isParticipantComplete(partner);
    const partnerName = partner ? this.getProgressName(partner, 'your partner') : '';
    const percent = currentUser.percentComplete || 0;

    // The partner's finish is the row's headline only where it leaves the
    // viewer as the one thing a shared artifact is waiting on.
    const partnerNudges = isInProgress && partnerBlocks && !!partner && partnerComplete;
    const guideEarned = EARNED_GUIDE_STATUSES.includes(path.guideStatus?.status);

    const summaryParts = this._buildPathRowSummary(path, {
      isCompleted, isInProgress, currentUser, partner, partnerComplete, partnerName, partnerNudges, percent
    });

    let status = null;
    if (path.sharingConfirmOwedByYou) {
      status = { kind: 'confirm', label: 'Confirm sharing' };
    } else if (path.sharingConfirmOwedByPartner && partner) {
      status = { kind: 'waiting', label: `${partnerName} to confirm` };
    } else if (isCompleted && partnerBlocks && partner && !partnerComplete) {
      status = { kind: 'waiting', label: `Waiting on ${partnerName}` };
    } else if (isCompleted) {
      // The payoff, said once: the fact line no longer repeats it.
      status = { kind: 'complete', label: guideEarned ? 'Guide ready' : 'Complete' };
    } else if (partnerNudges) {
      status = { kind: 'nudge', label: `${partnerName} finished` };
    } else if (path.accessReason === 'premium_required') {
      status = { kind: 'premium', label: 'Premium' };
    } else if (path.accessReason === 'not_built') {
      // Spelled out rather than clipped to "Soon": the not-built run carries no
      // group label of its own (see `_orderedPathGroups`), so this chip is the
      // only place the sheet says what is withholding these rows.
      status = { kind: 'soon', label: 'Coming soon' };
    } else if ((path.phaseAccess ?? false) === false) {
      status = { kind: 'locked', label: 'Locked' };
    }

    return {
      id: path.id,
      name: path.name || '',
      imageUrl: this._pathImageUrl(path),
      status,
      // The raw percent, as the orbit draws it: the ring is a picture, and the
      // true number leads the fact line.
      ringPercent: isInProgress ? percent : null,
      badge: isCompleted ? 'check' : this._pathBadge(path),
      summaryParts,
      // Derived, never authored: one source for the line means the rendered
      // clauses and the plain-text form cannot drift.
      summary: summaryParts.map(part => part.text).join(ROW_FACT_SEPARATOR),
      description: this._pathRowDescription(path, { isCompleted, isInProgress }),
      dimmed: this._isPathDimmed(path),
      workable: this._isPathWorkable(path),
      badgeHtml: window.badgeService?.renderCardIndicator('discovery', path.id) || ''
    };
  }

  /**
   * The one metadata line under a row's title.
   *
   * **Every state returns the same genre of content — facts about this path —
   * so the second line is a scannable column rather than three formats.** The
   * three rungs report the only thing that can still be said at that stage:
   *
   *   - not started  → what it costs      `9 sections · about 3 hr`
   *   - in progress  → what is left       `40% · 4 sections left · about 50 min · Laurel at 70%`
   *   - complete     → when it was done   `You May 27 · Laurel Jun 5`
   *
   * What was EARNED (`Guide ready`) is the status chip's, not this line's, so
   * a finished row says it once.
   *
   * The CSS sets this line as metadata (13px/500, `--text-300`, tabular
   * numerals) rather than as the prose the shared `.session-card__summary`
   * otherwise carries, so it reads as a different register from the title
   * above it. Keep both halves in step: styling a fact line as prose is what
   * made the old mixed-genre version unreadable.
   *
   * @param {Object} path - Path listing entry
   * @param {Object} ctx - Pre-resolved state from `_buildPathRowModel`
   * @param {boolean} ctx.isCompleted - Path finished by the viewer
   * @param {boolean} ctx.isInProgress - Path in flight for the viewer
   * @param {Object} ctx.currentUser - Viewer's progress slice
   * @param {Object|null} ctx.partner - Partner's progress slice, or null (solo)
   * @param {boolean} ctx.partnerComplete - Partner finished the path
   * @param {string} ctx.partnerName - Partner display name
   * @param {boolean} ctx.partnerNudges - The partner's finish is already the
   *   row's status chip, so this line must not repeat it
   * @param {number} ctx.percent - Viewer's completion percent
   * @returns {Array<{text: string}>} Clauses in order
   * @private
   */
  _buildPathRowSummary(path, {
    isCompleted, isInProgress, currentUser, partner, partnerComplete, partnerName, partnerNudges, percent
  }) {
    if (isCompleted) {
      // The viewer is "You", never their own first name: nobody should have to
      // recognise themselves in a list.
      const clauses = [{ text: this._completionPhrase(currentUser, partner ? 'You' : null) }];
      // Only a partner who has ACTUALLY finished gets a completion clause.
      // `_completionPhrase` falls back to "<name> completed" whenever it has no
      // date, so an unfinished partner used to be reported as done — directly
      // contradicting the `Waiting on <name>` chip on the same row.
      if (partnerComplete) clauses.push({ text: this._completionPhrase(partner, partnerName) });
      return clauses;
    }

    if (isInProgress) {
      // The number leads: the tile's ring is only a picture of it.
      const clauses = [{ text: `${percent}%` }];
      const remaining = this._pathRemainingPhrase(path, {
        separator: ROW_FACT_SEPARATOR,
        minutePrefix: 'about '
      });
      if (remaining) clauses.push({ text: remaining });
      if (partner && !partnerNudges) {
        // Neutral news either way: a partner still working, or one who has
        // finished an individual path (nothing waits on the viewer there —
        // where it does, the status chip has already said so).
        clauses.push({
          text: partnerComplete
            ? `${partnerName} finished`
            : `${partnerName} at ${partner.percentComplete || 0}%`
        });
      }
      return clauses;
    }

    const scope = this._pathScopeClauses(path);
    if (scope.length > 0) return scope.map(text => ({ text }));

    const description = this._pathDescription(path);
    return description ? [{ text: description }] : [];
  }

  /**
   * What a path COSTS, for a row the viewer has not started, as clauses:
   * `9 sections · about 3 hr`. The one predicate for "does this path say what
   * it costs" — `_buildPathRowSummary` turns them into the unstarted rung's
   * clauses, and `_buildPathRowModel` asks whether there were any before
   * giving the row a separate description line. Deriving both from this list
   * is what keeps the two from disagreeing about which row shape applies.
   *
   * Sections and "about" minutes, not question counts: the same units the
   * in-flight row (`_pathRemainingPhrase`) and the hero use, so the sheet has
   * one vocabulary. `questionnairesTotal` is the required-questionnaire-row
   * count `sectionsRemaining` is subtracted from, so "9 sections" here becomes
   * "9 sections left" the moment the path is started. A payload that carries
   * no row count at all (none does) falls back to the question count rather
   * than saying nothing.
   *
   * @param {Object} path - Path listing entry
   * @returns {Array<string>} Clauses, possibly empty
   * @private
   */
  _pathScopeClauses(path) {
    const clauses = [];

    const { currentUser } = this.resolvePathProgress(path);
    const sections = currentUser.questionnairesTotal || 0;
    const questions = path.totalQuestions || 0;
    if (sections > 0) {
      clauses.push(`${sections} section${sections === 1 ? '' : 's'}`);
    } else if (questions > 0) {
      clauses.push(`${questions} question${questions === 1 ? '' : 's'}`);
    }

    const minutes = path.estimatedDuration || 0;
    if (minutes > 0) clauses.push(`about ${this.formatDuration(minutes)}`);

    // No "Generates a guide" clause. Nearly every path generates one, so it
    // never distinguishes the row it sits on — it is texture, and it cost the
    // line a third wrapped line at phone widths. What a path produces belongs
    // in the drawer, where there is room to say which guides and for whom
    // (Doug 2026-09-04).
    return clauses;
  }

  /**
   * The row's middle line — the prose one, between the title and the metadata
   * line. Every row that can carry one does, so the list has a single shape:
   *
   *   unstarted    the path's one-line tagline — what it IS (`cardContent.
   *                tagline`, authored to fit the row; the full description
   *                stays the drawer's and the hero's)
   *   completed    the earned guide's tagline — what it PRODUCED (`guideStatus.
   *                tagline`, generated per guide — a different field that
   *                happens to share the word), falling back to the description
   *                when no guide was earned
   *   in progress  none; the row is two lines, with the tile's ring as the
   *                picture of where it stands
   *
   * The completed rung is why this exists. Without it a finished path was the
   * only two-line row on the sheet, which read as a row missing something
   * rather than as a row with less to say. The guide's tagline is the better
   * line there than the description: it names the artifact the reader now has,
   * which is the news on a path they have finished.
   *
   * Suppressed on an unstarted path that declares no scope at all, because
   * `_buildPathRowSummary` has then already fallen back to this same sentence
   * as the metadata line and would otherwise print it twice.
   *
   * @param {Object} path - Path listing entry
   * @param {Object} ctx
   * @param {boolean} ctx.isCompleted - Path finished by the viewer
   * @param {boolean} ctx.isInProgress - Path in flight for the viewer
   * @returns {string} Plain text (the renderer escapes it), '' for no line
   * @private
   */
  _pathRowDescription(path, { isCompleted, isInProgress }) {
    if (isCompleted) {
      const earned = EARNED_GUIDE_STATUSES.includes(path.guideStatus?.status);
      return (earned && path.guideStatus?.tagline) || this._pathDescription(path);
    }
    if (isInProgress) return '';
    if (this._pathScopeClauses(path).length === 0) return '';
    return (path.cardContent?.tagline || '').trim() || this._pathDescription(path);
  }

  /**
   * A path's own description, whitespace-collapsed to one line.
   * @param {Object} path - Path listing entry
   * @returns {string} Plain text (the renderer escapes it)
   * @private
   */
  _pathDescription(path) {
    return (path.cardContent?.description || path.description || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * "<name> <date>" for a finished participant, degrading cleanly when either
   * the name (solo viewer) or the date is missing.
   * @param {Object} participant - Progress participant record
   * @param {string|null} name - Display name, or null for the solo phrasing
   * @returns {string} Plain text
   * @private
   */
  _completionPhrase(participant, name) {
    const date = this._formatRowDate(participant?.completedAt);
    if (name) return date ? `${name} ${date}` : `${name} completed`;
    return date ? `Completed ${date}` : 'Completed';
  }

  /**
   * A completion date for the ROW's fact line: `May 27`, month-name and day,
   * no year.
   *
   * Deliberately not `formatCompletedDate` (`5/27/26`), which stays the
   * drawer's format. The row prints two of these side by side under a path
   * name, where the slashed numerals were the one raw-looking thing on an
   * otherwise typeset sheet, and where a repeated year buys nothing: the
   * drawer is one tap away and carries the precise date on its state pill.
   *
   * @param {string} isoDate - ISO date string
   * @returns {string} e.g. `May 27`, or '' for a missing/unparseable date
   * @private
   */
  _formatRowDate(isoDate) {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  /**
   * The group label opening the Foundation-locked run of rows.
   *
   * The three group labels are quiet in-sheet headings, not floating pill
   * dividers: the rows live on one white sheet now, and a filled pill sitting
   * between hairline-separated rows reads as a card of its own.
   *
   * @returns {string} HTML string
   */
  renderLockedPathsNotice() {
    return this._renderPathGroupLabel('More paths unlock after Starting Your Journey');
  }

  /**
   * One quiet in-sheet group label, aligned to the row content above it, with
   * an optional trailing action pill. The pill is the group's ONE affordance:
   * it sits on the label, never on the rows, so a run of four locked rows
   * carries a single offer and each row stays a plain row (tap opens the
   * panel, as every row does). A label without an action keeps `role="note"`;
   * one carrying a button is a group header, not a note.
   * @param {string} title - Label copy
   * @param {Object} [opts]
   * @param {{id: string, label: string}|null} [opts.action=null] - Trailing
   *   pill: `id` lands in `data-action` (delegated from the list container in
   *   `attachPathsEventListeners`), `label` is its text
   * @returns {string} HTML string
   * @private
   */
  _renderPathGroupLabel(title, { action = null } = {}) {
    const actionHtml = action
      ? `<button type="button" class="discovery-path-group__action" data-action="${this.escapeHtml(action.id)}">${this.escapeHtml(action.label)}</button>`
      : '';
    return `
      <div class="discovery-path-group"${action ? '' : ' role="note"'}>
        <p class="discovery-path-group__title">${this.escapeHtml(title)}</p>${actionHtml}
      </div>
    `;
  }

  /**
   * PATH ACCESS — see backend/src/config/pathAccess.js.
   *
   * Separates paths that exist but sit outside the free-tier allowance
   * (`accessReason: 'premium_required'`). Unlike the Foundation-locked label
   * this one names something the user can act on today, which is why its group
   * sits above the unlabelled coming-soon run — and why it alone carries the
   * sheet's upgrade affordance: one "See Premium" pill trailing the label,
   * routed through `goToUpgrade(eventBus, 'pathGroup')`. The rows beneath it
   * stay plain rows; the panel each one opens carries its own button.
   *
   * @returns {string} HTML string
   */
  renderPremiumPathsNotice() {
    return this._renderPathGroupLabel('Available with Premium', {
      action: { id: PATH_GROUP_UPGRADE_ACTION, label: 'See Premium' }
    });
  }

  /**
   * Re-render the paths row list from freshly loaded data. Genuine data reloads
   * only — panel transitions go through `_refreshPathRowActive`, which patches
   * the active highlight in place. The orbit is a sibling of this node, so it
   * survives, but its discs are not re-derived here either.
   */
  _refreshPathsList() {
    const listEl = this.container.querySelector('[data-paths-list]');
    if (listEl) {
      listEl.innerHTML = this.renderPathsList();
    }
  }

  /**
   * Check if a path is urgent: the partner finished, the user hasn't, AND that
   * asymmetry blocks a shared artifact (`path.partnerCompletionBlocks`,
   * backend-computed from SHARED_GUIDES_ENABLED + the path's membership in
   * the shared-guide registry). On individual-only paths a finished
   * partner blocks nothing — informational, never urgent. Boolean() so an
   * absent field fails safe (not urgent).
   *
   * An outstanding disclosure-set confirm (`path.sharingConfirmOwedByYou`) is
   * deliberately NOT an urgency input: it is only ever owed once the viewer has
   * completed the path, and this predicate drives the urgency sort over the
   * in-progress bucket alone (see renderPathsList). Completed cards are
   * DECORATED for an owed confirm — the "Waiting on you" pill, the red badge and
   * the drawer line — never reordered; the completed bucket keeps its canonical
   * order.
   * @param {Object} path - Path data object
   * @returns {boolean} True if path is urgent
   */
  isPathUrgent(path) {
    if (!Boolean(path?.partnerCompletionBlocks)) return false;
    if (!path?.partnerProgress || path.partnerProgress.length < 2) return false;

    const { currentUser, partner } = this.resolvePathProgress(path);
    if (!partner) return false;

    const currentUserCompleted = (currentUser.percentComplete || 0) === 100;
    const partnerCompleted = (partner.percentComplete || 0) === 100;

    return partnerCompleted && !currentUserCompleted;
  }

  /**
   * (Re)bind the sticky header stack: title → segment nav → the active tab's
   * filter bar (the Progress tab contributes no sticky slice). Idempotent — render()
   * replaces `container.innerHTML`, so the scroll root is a new node every time
   * and the previous controller must be released first.
   */
  _initStickyStack() {
    this.stickyStack?.destroy();
    this.stickyStack = null;
    const root = this.container.querySelector('.explore-view--list');
    if (root) this.stickyStack = new StickyStack(root);
  }

  /**
   * Attach event listeners
   */
  attachEventListeners() {
    Tooltip.setupAll(this.container, '[data-tooltip]', { position: 'below' });

    // Mobile fixed-action CTA (desktop's toolbar twin is bound in
    // _mountToolbarAction). render() rebuilds the node, so its listener goes
    // with it — no teardown to track. Tab changes only swap `.share-content`,
    // leaving this bar and its binding intact.
    const beginBtn = this.container.querySelector('[data-action="begin-discovery"]');
    if (beginBtn) {
      beginBtn.addEventListener('click', () => this.handleBeginSharing());
    }

    // Tab bar clicks
    const navItems = this.container.querySelectorAll(NAV_ITEM_SELECTOR);
    navItems.forEach(item => {
      item.addEventListener('click', () => {
        const tab = item.dataset.tab;
        if (tab && tab !== this.currentTab) {
          this.handleTabChange(tab);
        }
      });
    });

    // Bind whichever tab body render() just painted.
    this._bindTabEvents(this.currentTab);
  }

  /**
   * Handle tab change.
   *
   * Pushes the new tab to the URL via `page.replace`. The route handler
   * re-fires applyURLParams which calls back into this method with
   * `skipUrlPush: true` to avoid a loop.
   *
   * @param {string} tab - New tab identifier
   * @param {Object} [options]
   * @param {boolean} [options.skipUrlPush=false] - Set when called from
   *   applyURLParams to avoid re-pushing the URL we just read from.
   */
  async handleTabChange(tab, { skipUrlPush = false } = {}) {
    if (!skipUrlPush) {
      // A tab change implicitly closes any open detail panel — drop all
      // detail params from the URL.
      const url = new URL(window.location.href);
      url.searchParams.set('tab', tab);
      url.searchParams.delete('path');
      url.searchParams.delete('topic');
      url.searchParams.delete('thread');
      url.searchParams.delete('threadStatus');
      page.replace(url.pathname + url.search);
      return;
    }

    this.currentTab = tab;

    // Update nav item active states
    const navItems = this.container.querySelectorAll(NAV_ITEM_SELECTOR);
    navItems.forEach(item => {
      const isActive = item.dataset.tab === tab;
      item.classList.toggle(NAV_ACTIVE_CLASS, isActive);
      item.setAttribute('aria-selected', isActive.toString());
    });

    // Load data if switching to specific tabs
    if (tab === 'threads' && this.openThreads.length === 0 && !this.isLoadingQuestions) {
      await this.loadQuestionsData();
    }
    if (tab === 'paths' && this.paths.length === 0 && !this.isLoadingPaths) {
      await this.loadPathsData();
    }

    // Tear down per-tab components so leftover listeners don't accumulate.
    if (this.questionsFilterBar) {
      this.questionsFilterBar.destroy();
      this.questionsFilterBar = null;
    }

    // Update content. Both hero visuals are destroyed unconditionally: leaving
    // their tab drops the mount node, and bindProgressEvents /
    // attachPathsEventListeners (below) would otherwise be the only things that
    // ever release the instances.
    const contentContainer = this.container.querySelector('.share-content');
    if (contentContainer) {
      this._destroyPolarChart();
      this._destroyPathOrbit();
      contentContainer.innerHTML = this.renderTabContent();
      this._bindTabEvents(tab);
      this._showOrientationBanner(tab);
    }
  }

  /**
   * Bind the listeners (and mount the hero visual) for whichever tab body was
   * just painted. The one dispatch every paint goes through — `render()`,
   * `handleTabChange`, the badge re-render — so a tab can never be rendered
   * unbound. `currentTab` survives deactivation, so a return visit that lands
   * straight on Paths or Threads (browser Back, `?tab=` deep links) repaints
   * that tab from `render()`; binding only Progress there left the rows dead.
   * Each binder is destroy-first / dedupe-safe, so re-running one is fine.
   * @param {string} tab - 'progress' | 'paths' | 'threads'
   * @private
   */
  _bindTabEvents(tab) {
    if (tab === 'progress') this.bindProgressEvents();
    else if (tab === 'paths') this.attachPathsEventListeners();
    else if (tab === 'threads') this.attachQuestionsEventListeners();
  }

  static TAB_TO_INFO_TAB = {
    progress: 'progress',
    paths: 'paths',
    threads: 'threads',
  };

  _showOrientationBanner(tab) {
    if (this.orientationBanner) {
      this.orientationBanner.destroy();
      this.orientationBanner = null;
    }
    const message = DiscoverySection.ORIENTATION_MESSAGES[tab];
    if (!message) return;
    const contentContainer = this.container.querySelector('.share-content');
    if (!contentContainer) return;
    const infoTab = DiscoverySection.TAB_TO_INFO_TAB[tab] || 'progress';
    this.orientationBanner = new OrientationBanner({
      container: contentContainer,
      key: `discovery-${tab}`,
      message,
      linkText: 'Learn more',
      onLinkClick: () => this.showPageInfo(infoTab),
    });
    this.orientationBanner.show();
  }

  static PAGE_INFO_TABS = {
    progress: {
      label: 'Progress',
      content: `
        <h4 class="page-info-detail__subtitle">Tracking Your Growth</h4>
        <p class="page-info-detail__text">
          The Progress view maps how our understanding of your relationship is
          developing. Each wedge is a topic — the further it reaches and the
          deeper its color, the more we've explored together. The topic list
          below the map carries the same depth, plus a line on what we
          understand about each area so far.
        </p>
        ${renderPageInfoFaq([
          {
            q: 'How do the wedges grow?',
            a: `Every assessment you complete and conversation you have adds to
              what we've learned — the map reflects that as it happens.`,
          },
          {
            q: 'Do I have to explore every topic?',
            a: `No. The map just shows where understanding runs deep and where
              it's still thin, so you can choose where to go next.`,
          },
          {
            q: 'Where does each topic’s summary line come from?',
            a: `It's drawn from what we've actually learned about that area —
              your assessments and conversations — and updates as
              understanding builds.`,
          },
        ])}
      `,
    },
    paths: {
      label: 'Paths',
      content: `
        <h4 class="page-info-detail__subtitle">Guided Journeys</h4>
        <p class="page-info-detail__text">
          Paths are curated sequences of related topics that build understanding
          in a specific area of your relationship — each one a series of
          assessments and guided conversations that build on each other.
        </p>
        ${renderPageInfoFaq([
          {
            q: 'Which path should I start with?',
            a: `Whichever resonates with where you are right now — there's no
              required order.`,
          },
          {
            q: 'Can I switch paths?',
            a: `Yes, at any time. Your progress is saved, and you can pick a
              path back up where you left off.`,
          },
          {
            q: 'Do I have to finish in one sitting?',
            a: 'No. Work at your own pace — each step saves as you go.',
          },
        ])}
      `,
    },
    threads: {
      label: 'Threads',
      content: `
        <h4 class="page-info-detail__subtitle">Deeper Conversations</h4>
        <p class="page-info-detail__text">
          Threads are open-ended questions that emerge from your assessments. When your
          responses surface something worth exploring further, a thread is created to
          guide a deeper conversation about that topic.
        </p>
        <p class="page-info-detail__text">
          Unlike structured assessments, threads are conversational — they give you space
          to share context, nuance, and feelings that multiple-choice questions can't
          capture. This is where some of the richest understanding is built.
        </p>
      `,
    },
  };

  /**
   * Show page info detail view, optionally scrolled to a specific tab.
   * @param {string} [activeTab='progress'] - Which sub-tab to activate
   */
  showPageInfo(activeTab = this.currentTab) {
    // BETA PATH GATE — mirror the hidden Threads pill (_renderNavBar): while
    // the gate is on, the info panel doesn't advertise a tab the page hides.
    // Remove after beta. (An activeTab of 'threads' falls back to the first
    // tab inside buildPageInfoPanel.)
    const tabs = { ...DiscoverySection.PAGE_INFO_TABS };
    if (this.betaPathGate) delete tabs.threads;

    const content = buildPageInfoPanel({
      title: 'About Discovery',
      intro: `Discovery is where we build understanding together. Through guided
        questionnaires and conversations, we explore the topics and patterns
        that matter most to you — helping shape a therapeutic experience
        tailored to your unique needs.`,
      tabs,
      activeTab
    });

    // Discovery alone tracks a page-level selection: opening page-info clears any
    // active topic/path card highlight. Practices and Library have no equivalent,
    // so this stays inline rather than becoming a hook on the shared builder.
    this.currentSelection = { kind: 'page-info', id: 'page-info' };
    this._panelOpen = true;
    this._refreshActiveStates();
    eventBus.emit('right-panel:open', {
      content,
      contentType: 'discovery-info'
    });
  }

  /**
   * Parameterless guided-discovery entry — ModeOrchestrator picks the path.
   * Three callers: the "Begin discovery" action in both its desktop (toolbar)
   * and mobile (fixed bottom bar) forms, and the recommended-next pill's
   * fallback when the peek produced no specific recommendation.
   */
  handleBeginSharing() {
    eventBus.emit('navigate', {
      page: 'chat',
      mode: 'DISCOVERY'
      // No explicit pathId here -> triggers guided discovery path in ModeOrchestrator
    });
  }

  // ===== PATHS TAB METHODS =====

  /**
   * Load paths with couple progress from backend
   */
  async loadPathsData() {
    this.isLoadingPaths = true;
    this.pathsLoadError = null;

    try {
      let result;
      if (this.coupleId) {
        result = await this.discoveryAPI.getPathsWithCoupleProgress(this.coupleId);
      } else {
        result = await this.discoveryAPI.getPathsWithUserProgress();
      }
      this.paths = result?.data?.paths || [];
    } catch (error) {
      console.error('[DiscoverySection] Error loading paths:', error);
      this.paths = [];
      this.pathsLoadError = 'Failed to load paths';
    } finally {
      this.isLoadingPaths = false;
    }

    // Re-resolve a URL ?path= that may have been deep-linked before paths
    // finished loading.
    this._resolvePendingDetailFromURL();
  }

  /**
   * Bind the Paths tab: mount the hero orbit, wire the rows through the shared
   * hero binder, and delegate the tab's remaining controls. Runs on every
   * paths render, so everything here is either delegated from a
   * freshly-rendered node or torn down first.
   */
  attachPathsEventListeners() {
    // Release the work-entry guard on every rebind — the Progress tab gets this
    // for free by scoping `workingOnNext` to bindProgressEvents. A hand-off that
    // was intercepted rather than followed (a premium gate, an emit that threw)
    // leaves the section mounted, so `deactivate()` never runs and the flag
    // would otherwise latch both the pill and the panel CTA dead.
    this._workingOnPath = false;

    const tab = this.container.querySelector('[data-tab-content="paths"]');

    // Path orbit. Fail-soft, same contract as the polar chart: a visual that
    // can't mount must still leave the rows fully usable.
    this._destroyPathOrbit();
    const orbitMount = tab?.querySelector('[data-path-orbit]');
    if (orbitMount) {
      try {
        const { center, satellites } = this._buildPathOrbitModel();
        this.pathOrbit = new PathOrbit(orbitMount, {
          paths: satellites,
          centerPath: center,
          onHover: (pathId) => this._setPathRowHot(pathId),
          onClick: (pathId) => this._navigateToPath(pathId)
        });
      } catch (err) {
        console.warn('[DiscoverySection] path orbit mount failed:', err);
        this.pathOrbit = null;
      }
    }

    // Hero pill — the page's ONE primary action (discovery.css), so it starts
    // the work rather than opening a drawer: its label is a verb (`Continue` /
    // `Begin`) and `_workOnPath` is the same entry the panel CTA uses. The
    // all-complete header state renders no pill at all, so neither branch below
    // can reach the hand-off. `_workOnPath` owns the reentrancy guard.
    const nextTile = tab?.querySelector('[data-action="paths-next"]');
    if (nextTile) {
      nextTile.addEventListener('click', () => {
        const path = (this.paths || []).find(p => p.id === nextTile.dataset.pathId);
        if (!path) return;
        this._workOnPath(path);
      });
    }

    const listEl = this.container.querySelector('[data-paths-list]');
    if (!listEl) return;

    // Path rows — the shared hero binder (delegated from the stable list
    // container so the handlers survive in-place active-highlight updates).
    bindTopicRows(listEl, {
      onOpen: (pathId) => this._navigateToPath(pathId),
      getChart: () => this.pathOrbit,
      ...PATH_ROW_BINDING
    });

    // PATH ACCESS — the Premium group label's "See Premium" pill. Delegated
    // from the same stable container so it survives list re-renders. The pill
    // sits on the group label, outside every row, so the row binder above
    // never sees the click as a row open.
    listEl.addEventListener('click', (e) => {
      const pill = e.target.closest(`[data-action="${PATH_GROUP_UPGRADE_ACTION}"]`);
      if (!pill) return;
      e.preventDefault();
      goToUpgrade(eventBus, 'pathGroup');
    });
  }

  /**
   * The orbit's discs, derived from the same ordered model the rows render
   * from, so the ring order and the row order cannot drift. The path the aside
   * names (`_currentHeaderPath`) anchors the centre, so the biggest picture and
   * the biggest words agree; when nothing is current the Foundations path
   * takes the middle. Everything else orbits in row order. The satellite
   * filter is by identity, which holds because `_flatOrderedPaths` never
   * clones entries.
   *
   * @returns {{center: Object|null, satellites: Array<Object>}} PathOrbit input
   * @private
   */
  _buildPathOrbitModel() {
    const ordered = this._flatOrderedPaths();
    const toDisc = (path) => {
      const { currentUser } = this.resolvePathProgress(path);
      return {
        id: path.id,
        name: path.name || '',
        imageUrl: this._pathImageUrl(path),
        percent: currentUser.percentComplete || 0,
        dimmed: this._isPathDimmed(path),
        complete: Boolean(path.isCompleted),
        locked: this._pathBadge(path) === 'lock'
      };
    };

    const center = this._currentHeaderPath()
      || ordered.find(p => p.id === ORBIT_CENTER_PATH_ID)
      || null;
    return {
      center: center ? toDisc(center) : null,
      satellites: ordered.filter(p => p !== center).map(toDisc)
    };
  }

  /**
   * Format an estimated duration for display: `45 min`, `3 hr`, `2 hr 15 min`.
   *
   * Hours carry their remainder as minutes rather than as a fraction of an
   * hour. The old form divided the rounded minutes straight through and
   * printed the quotient, so anything off the hour surfaced as a decimal —
   * "2.25 hours", "1.5 hours" — which is a unit nobody says out loud and which
   * read as spurious precision on what is only an estimate. Rounding to the
   * quarter hour is kept; only its presentation changes.
   *
   * @param {number} minutes - Duration in minutes
   * @returns {string} Formatted duration
   */
  formatDuration(minutes) {
    if (!minutes || minutes <= 0) return '0 min';

    // Sub-hour durations are reported as given; only hour-scale estimates are
    // rounded, where a stray minute is noise.
    const rounded = minutes < 60 ? minutes : Math.round(minutes / 15) * 15;
    if (rounded < 60) return `${rounded} min`;

    const hours = Math.floor(rounded / 60);
    const remainder = rounded % 60;
    return remainder === 0 ? `${hours} hr` : `${hours} hr ${remainder} min`;
  }

  /**
   * Format date as short M/D/YY format
   * @param {string} isoDate - ISO date string
   * @returns {string} Formatted date string (e.g., "12/5/23")
   */
  formatShortDate(isoDate) {
    if (!isoDate) return '';

    const date = new Date(isoDate);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  }

  /**
   * Format a path completion date for completed-card metadata.
   * @param {string|Date|null} isoDate - Completion timestamp.
   * @returns {string} Formatted date, or empty string when unavailable.
   */
  formatCompletedDate(isoDate) {
    if (!isoDate) return '';

    const date = new Date(isoDate);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit'
    });
  }

  /**
   * Whether a participant has finished a path. The single definition of that
   * test — the progress slice carries both a percentage and an explicit flag,
   * and the row sheet, the detail panel and `renderParticipantCompletionLine`
   * must never disagree about which one won.
   * @param {Object} participant - Progress participant record.
   * @returns {boolean} True when the participant has completed the path.
   */
  isParticipantComplete(participant) {
    return (participant?.percentComplete || 0) === 100 || participant?.isComplete === true;
  }

  /**
   * Build the long-form completion phrase for a participant, for the stacked
   * completion lines in the path detail panel.
   * @param {Object} participant - Progress participant record.
   * @param {string} name - Display name.
   * @returns {string} e.g. "Laurel completed 6/5/26".
   */
  renderCompletionLabel(participant, name) {
    const date = this.formatCompletedDate(participant?.completedAt);
    return `${name} completed${date ? ` ${date}` : ''}`;
  }

  /**
   * Render a stacked participant completion line with a check/X icon. Used by
   * the completed branch of the path detail panel; the row sheet carries the
   * same facts as a one-line `summary` from `_buildPathRowModel`.
   *
   * `neutralMessage` is a THIRD tone, distinct from complete/incomplete: no
   * check, no × glyph, no warning color — reuses the existing
   * `--remaining` neutral-info styling (`var(--text-200)`, no icon). It exists
   * for a participant who already finished the path but has a private,
   * outstanding choice that isn't the viewer's business to see as a warning
   * (e.g. the partner's own outstanding shared-guide disclosure-set confirm —
   * A3's neutral-informational ceiling: never actionable-red toward the
   * viewer about someone else's pending choice). `forceIncomplete` stays
   * reserved for the viewer's OWN outstanding, actionable state.
   *
   * @param {Object} participant - Progress participant record.
   * @param {Object} options - Render options.
   * @param {string} options.fallbackName - Fallback display name.
   * @param {string} [options.incompleteMessage] - Incomplete-state (×, warning-tone) message.
   * @param {string} [options.neutralMessage] - Neutral-tone message (no icon, no warning
   *   color) — takes precedence over the complete/incomplete branches entirely.
   * @param {string} [options.className] - Extra class for the line.
   * @param {boolean} [options.forceIncomplete] - Render the incomplete (×,
   *   warning-tone) line even though the participant finished the path — for
   *   the VIEWER's own outstanding, actionable shared-guide disclosure-set
   *   confirm only. Never use this for someone else's pending choice; use
   *   `neutralMessage` instead.
   * @returns {string} HTML string.
   */
  renderParticipantCompletionLine(participant, { fallbackName, incompleteMessage, neutralMessage, className = '', forceIncomplete = false } = {}) {
    const name = this.getProgressName(participant, fallbackName || 'Your partner');
    const extraClass = className ? ` ${className}` : '';

    if (neutralMessage) {
      return `
        <div class="path-detail__progress-info-line path-detail__progress-info-line--remaining${extraClass}">
          <span>${this.escapeHtml(neutralMessage)}</span>
        </div>
      `;
    }

    const isComplete = !forceIncomplete && this.isParticipantComplete(participant);

    if (isComplete) {
      return `
        <div class="path-detail__progress-info-line path-detail__progress-info-line--complete${extraClass}">
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
            <use href="#icon-checkmark"/>
          </svg>
          <span>${this.escapeHtml(this.renderCompletionLabel(participant, name))}</span>
        </div>
      `;
    }

    return `
      <div class="path-detail__progress-info-line path-detail__progress-info-line--incomplete${extraClass}">
        <span class="path-detail__progress-info-x" aria-hidden="true">×</span>
        <span>${this.escapeHtml(incompleteMessage || `${name} has not completed this path yet`)}</span>
      </div>
    `;
  }

  /**
   * Resolve the requesting user's and partner's progress slices for a path.
   * Prefers backend-emitted `userProgress`, then matches by `userId`, and
   * finally falls back to array order. Returns `partner` as null for solo users.
   * @param {Object} path - Path data object.
   * @returns {{ currentUser: Object, partner: Object|null }}
   */
  resolvePathProgress(path) {
    const userId = this.currentUserData?.id;
    const slots = path?.partnerProgress || [];
    const currentUser = path?.userProgress
      || (userId && slots.find(p => p.userId === userId))
      || slots[0]
      || {};
    const partner = (userId && slots.find(p => p.userId !== userId))
      || (slots.length >= 2 ? slots.find(p => p !== currentUser) : null)
      || null;
    return { currentUser, partner };
  }

  /**
   * Get a display name for the current user.
   * @returns {string} Current user's first name or a generic fallback.
   */
  getCurrentUserDisplayName() {
    const firstName = this.currentUserData?.firstName;
    if (firstName && !isGuestName(firstName)) return firstName;
    return 'You';
  }

  /**
   * Get a participant display name for progress metadata.
   * @param {Object} participant - Progress participant record.
   * @param {string} fallbackName - Fallback display name.
   * @returns {string} Display name.
   */
  getProgressName(participant, fallbackName) {
    const firstName = participant?.firstName;
    if (firstName && !isGuestName(firstName)) return firstName;
    return fallbackName;
  }

  /**
   * Open a generated guide in the Discovery right panel.
   * @param {string} guideId - Guide definition ID.
   */
  async showGuideDetailView(guideId) {
    if (!guideId) return;
    // Toggle / dedup — see _navigateToTopic. The guide panel isn't URL-routed,
    // so this closes it directly rather than through a URL change.
    if (this._panelOpen && this._isSelected('guide', guideId)) {
      eventBus.emit('right-panel:close');
      return;
    }

    this.selectedGuide = null;
    this.currentSelection = { kind: 'guide', id: guideId };
    this._panelOpen = true;
    this._markGuideViewed(guideId);
    this._refreshActiveStates();

    const content = document.createElement('div');
    content.innerHTML = this.renderGuideDetailPanel();
    this._detailContent = content;

    eventBus.emit('right-panel:open', {
      title: '',
      titleAlign: 'left',
      optionsDropdown: this._buildGuideOptions(guideId),
      headerMenu: buildGuideContentsMenu(this.selectedGuide),
      content,
      contentType: 'library-guide',
      // Guide-contents-rail plan D10/D12: the rail wants room for its own
      // column, so guide panels nudge wider on open (RightPanelManager
      // honours this at open time, never shrinking a wider dragged width).
      preferredWidth: 880,
      // The panel disposes the outgoing content's spy before it swaps or
      // closes; routed through the host's disposer so both share one owner,
      // keyed on THIS node because that dispose lands 130ms late.
      onDispose: () => this._disposeContentsSpyFor(content)
    });

    // The loading body carries no rail, so this arms nothing — it exists so a
    // guide that never reaches the fetch below still leaves no stale spy.
    this._armContentsSpy(content);

    try {
      const guide = await API.getGuide(guideId);
      if (guide) {
        this.selectedGuide = guide;
      }
    } catch (error) {
      console.error('[DiscoverySection] Error loading guide:', error);
    }

    if (this._detailContent && this._isSelected('guide', guideId)) {
      this._detailContent.innerHTML = this.renderGuideDetailPanel();
      this._syncGuideOptions(guideId);
      // The replacement built the real rail; arm the spy over it.
      this._armContentsSpy(this._detailContent);
    }
  }

  /**
   * Options dropdown for this section's two guide panels — Copy/Share plus
   * Archive/Unarchive, from the same shared builder /library and /search use.
   * No guide surface offers Delete.
   *
   * The Archive label reads `this.selectedGuide`, which is null while the
   * panel's `API.getGuide()` is still in flight; the menu therefore opens
   * saying "Archive" and is corrected by `_syncGuideOptions` the moment the
   * fetch lands. Discovery has no guides list to source a summary row from
   * (guides live here only as a side door off a path), so there is nothing
   * better to read at open time.
   * @param {string} guideId - Guide definition ID
   * @returns {Object} `optionsDropdown` payload
   * @private
   */
  _buildGuideOptions(guideId) {
    return buildGuideManagementDropdown(() => this.selectedGuide, {
      archived: !!this.selectedGuide?.archivedAt,
      onOption: (action) => this._handleGuidePanelOption(action, guideId)
    });
  }

  /**
   * Re-patch the open panel's Options dropdown in place (no re-open, no
   * re-slide) so its Archive/Unarchive label matches the guide now in hand.
   * Used both after the initial fetch resolves and after an archive toggle.
   * @param {string} guideId - Guide definition ID
   * @private
   */
  _syncGuideOptions(guideId) {
    eventBus.emit('right-panel:set-options-dropdown', this._buildGuideOptions(guideId));
    // The contents menu needs the loaded guide's section list, which does not
    // exist at panel-open time on this surface.
    eventBus.emit('right-panel:set-header-menu', buildGuideContentsMenu(this.selectedGuide));
  }

  /**
   * Options-dropdown handler for both guide panels (Archive/Unarchive — the
   * only management action any guide surface offers). Archive flips the label
   * in place.
   *
   * It broadcasts on the contentSync bridge after confirmed success (local
   * state first) — this section holds no guide *list* of its own, but the
   * sidebar, `/search`, and the `/insights` guides tab all render the same
   * guide and would otherwise show a stale row. This section has no inbound
   * `guide` listener for the same reason: nothing here caches one.
   * @param {'archive'} action
   * @param {string} guideId - Guide definition ID
   * @private
   */
  async _handleGuidePanelOption(action, guideId) {
    if (action !== 'archive') return;
    const next = !this.selectedGuide?.archivedAt;
    const result = await API.archiveGuides([guideId], next);
    if (!result || result.success === false) {
      notifyArchiveFailed({ count: 1, type: 'guide', archived: next });
      return;
    }
    if (this.selectedGuide) {
      this.selectedGuide.archivedAt = next ? new Date().toISOString() : null;
    }
    this._syncGuideOptions(guideId);
    emitContentArchived('guide', guideId, next);
    notifyArchived({ count: 1, type: 'guide', archived: next });
  }

  /**
   * Render the guide detail panel content. Thin delegate to the shared
   * `renderGuideDetailBody` (guideDrillHelper.js) — the same renderer
   * `/library` and `/search` use — so all three guide-detail surfaces share
   * one implementation (this section previously carried its own copy with a
   * hand-rolled regex markdown renderer).
   * @returns {string} HTML string.
   */
  renderGuideDetailPanel() {
    return renderGuideDetailBody(this.selectedGuide);
  }

  /**
   * Apply URL parameters — drives tab and detail panel state from URL.
   *
   * Bidirectional for the detail panel:
   *  - `path` or `topic` present → open or swap to that detail
   *  - both absent + panel currently showing path/topic → close
   *
   * Page-info detail (contentType 'discovery-info') is not URL-routed.
   */
  async applyURLParams(params) {
    // Detail params imply a tab — `?path=` lives on `paths`, `?topic=` on
    // `progress`, `?thread=` on `threads`. Auto-route the tab so deep links
    // like `/discovery?thread=5` land on the correct surface.
    let effectiveTab = params.tab;
    if (params.path) effectiveTab = 'paths';
    else if (params.topic) effectiveTab = 'progress';
    else if (params.thread) effectiveTab = 'threads';

    if (effectiveTab && effectiveTab !== this.currentTab) {
      await this.handleTabChange(effectiveTab, { skipUrlPush: true });
    }

    if (params.path) {
      if (this._panelOpen && this._isSelected('path', params.path)) return;
      const path = this.paths.find(p => p.id === params.path);
      if (path) {
        // Every KNOWN path routes through, including withheld and phase-locked
        // ones: their panel opens with no work-entry button and an availability
        // marker instead (`showDetailView`). Access is enforced on the action,
        // not on the route — the only thing scrubbed here is an id that names
        // no path at all.
        this.showDetailView(path, { skipUrlPush: true });
      } else if (this.paths.length > 0) {
        // Paths loaded but slug unknown — scrub URL.
        const url = new URL(window.location.href);
        url.searchParams.delete('path');
        page.replace(url.pathname + (url.search ? url.search : ''));
      }
      // If paths haven't loaded yet, _resolvePendingDetailFromURL will
      // re-trigger this once data arrives.
    } else if (params.topic) {
      if (this._panelOpen && this._isSelected('topic', params.topic)) return;
      const topic = this.progressTopics.find(t => t.id === params.topic);
      if (topic) {
        this.showTopicDetailView(params.topic, { skipUrlPush: true });
      } else if (this.progressTopics.length > 0) {
        const url = new URL(window.location.href);
        url.searchParams.delete('topic');
        page.replace(url.pathname + (url.search ? url.search : ''));
      }
    } else if (params.thread) {
      const threadNumber = parseInt(params.thread, 10);
      if (!Number.isFinite(threadNumber)) return;

      // A sidecar `threadStatus` (cross-surface deep links, e.g. the sidebar
      // Starred section) names the status tab the thread actually lives in —
      // reload under that filter first so an addressed/archived starred
      // thread still resolves instead of silently failing under the Open
      // tab's default 'unaddressed' load.
      if (params.threadStatus && params.threadStatus !== this.questionsFilter) {
        this.questionsFilter = params.threadStatus;
        await this.loadQuestionsData();
        if (this.currentTab === 'threads') {
          this._mountQuestionsFilterBar();
          this._refreshQuestionsList();
        }
      }

      const thread = this.openThreads.find(t => t.threadNumber === threadNumber);
      if (this._panelOpen && thread && this._isSelected('thread', thread.id)) return;
      if (thread) {
        this.showThreadDetailView(thread.id, { skipUrlPush: true });
      } else if (this.openThreads.length > 0) {
        // Threads loaded but number unknown — scrub URL.
        const url = new URL(window.location.href);
        url.searchParams.delete('thread');
        url.searchParams.delete('threadStatus');
        page.replace(url.pathname + (url.search ? url.search : ''));
        window.app?.modules?.rightPanelManager?.closeStaleHandoff();
      }
      // If threads haven't loaded yet, _resolvePendingDetailFromURL will
      // re-trigger this once data arrives.
    } else if (this._panelOpen
        && (this.currentSelection?.kind === 'path'
          || this.currentSelection?.kind === 'topic'
          || this.currentSelection?.kind === 'thread')) {
      eventBus.emit('right-panel:close');
    }
  }

  /**
   * Re-resolve a URL detail param that wasn't resolvable on the first
   * applyURLParams pass (e.g., deep link to ?path=... before the paths tab
   * had loaded its data). Called from the end of loadPathsData /
   * loadQuestionsData.
   */
  _resolvePendingDetailFromURL() {
    if (!this.isActive) return;
    const params = new URLSearchParams(window.location.search);
    const wantedPath = params.get('path');
    const wantedTopic = params.get('topic');
    const wantedThread = params.get('thread');
    if (wantedPath && (!this._panelOpen || !this._isSelected('path', wantedPath))) {
      this.applyURLParams({ path: wantedPath });
    } else if (wantedTopic && (!this._panelOpen || !this._isSelected('topic', wantedTopic))) {
      this.applyURLParams({ topic: wantedTopic });
    } else if (wantedThread) {
      const wantedNum = parseInt(wantedThread, 10);
      const matchingThread = this.openThreads.find(t => t.threadNumber === wantedNum);
      if (matchingThread && (!this._panelOpen || !this._isSelected('thread', matchingThread.id))) {
        this.applyURLParams({ thread: wantedThread });
      }
    }
  }

  /**
   * Show path detail view in right panel.
   * @param {Object} path - Path data object
   * @param {Object} [_options] - Reserved for caller flags (e.g. `skipUrlPush`)
   */
  showDetailView(path, _options = {}) {
    this.currentPath = path;

    const cardContent = path.cardContent || {};
    const description = cardContent.description || '';
    // Sections, the unit the nav rows and hero use; the question count is the
    // fallback for a payload that carries no row count (none does).
    const numSections = this.resolvePathProgress(path).currentUser.questionnairesTotal || 0;
    const numQuestions = path.totalQuestions || 0;
    const numMinutes = path.estimatedDuration || 0;
    const durationText = this.formatDuration(numMinutes);
    const cardImage = cardContent.image;
    const imageHTML = cardImage
      ? `<img src="assets/images/paths/${this.escapeHtml(cardImage)}" alt="${this.escapeHtml(path.name)}" class="path-detail__image-img">`
      : '';

    const isInProgress = path.isInProgress || false;
    const isCompleted = path.isCompleted || false;
    const ctaLabel = isInProgress ? 'Continue this path' : 'Begin this path';
    // Every path row is tappable, so this panel also has to speak for paths
    // that cannot be worked today (withheld or phase-locked). Those keep the
    // "What to expect" block — same shell, bullets for a body — and close on an
    // availability marker, in place of the progress block and the sections
    // list. The work-entry button's slot goes to an upgrade button on a
    // Premium path and to nothing on the other two kinds.
    const workable = this._isPathWorkable(path);
    const premiumRequired = path.accessReason === 'premium_required';

    // Meta slot under the title (detail-page-style-guide §4): gray fact pills,
    // with completion riding the row as a tinted state tag — never its own
    // status line. The CTA is the panel header's primary button only.
    const { currentUser: currentUserProgress, partner: partnerProgress } = this.resolvePathProgress(path);
    let statePillHTML = '';
    if (isCompleted) {
      const completedDate = this.formatCompletedDate(currentUserProgress?.completedAt);
      statePillHTML = `
        <span class="tag tag--complete">
          <svg width="11" height="11" viewBox="0 0 16 16" aria-hidden="true">
            <use href="#icon-checkmark"/>
          </svg>
          Completed${completedDate ? ` · ${this.escapeHtml(completedDate)}` : ''}
        </span>
      `;
    }
    const metaPillsHTML = `
      <div class="sd-metadata">
        ${statePillHTML}
        ${numSections > 0
          ? `<span class="sd-metadata__text"><b>${numSections}</b> Section${numSections === 1 ? '' : 's'}</span>`
          : `<span class="sd-metadata__text"><b>${numQuestions}</b> Questions</span>`}
        <!-- Not split into a bold number + label like the pill above: a
             duration can now carry two numbers ("2 hr 15 min"), and bolding
             only the first read as a typo. -->
        <span class="sd-metadata__text">${this.escapeHtml(durationText)}</span>
        ${path.kind === 'guide' ? '<span class="sd-metadata__text">Generates Guide</span>' : ''}
      </div>
    `;

    // "What to expect" → "Generated Guide" when the path is complete and a
    // guide is ready/stale to view. The card shares /library's artifact-style
    // guide-row format (see _renderPathGuideCard).
    const guideStatus = path.guideStatus?.status || null;
    const hasReadyGuide = isCompleted
      && !!path.guideDefinitionId
      && (guideStatus === 'READY' || guideStatus === 'STALE');
    let whatToExpectHTML = '';
    if (hasReadyGuide) {
      whatToExpectHTML = `
        <div class="sd-section-header">Generated guide</div>
        ${this._renderPathGuideCard(path)}
      `;
    } else if (path.description) {
      whatToExpectHTML = this._renderExpectBlock(
        `<p class="sd-summary">${this.escapeHtml(path.description)}</p>`,
        imageHTML
      );
    }

    // Progress block:
    //  - In progress: blue gradient bar at current %, "X sections, Y minutes
    //    remaining" (gradient = progress semantics, style guide §2)
    //  - Completed: participant completion lines only — the "Completed" state
    //    pill in the meta row carries the date, and a full bar answers nothing
    //    (the nav card drops its bar on completion for the same reason)
    //  - Not started: omitted (no header rendered above it)
    let progressBlockHTML = '';
    // The zone's at-a-glance chip (shown only while the zone is collapsed):
    // the path's completion figure, since "how far along am I?" is what this
    // zone answers. The not-started case has no figure — its chip is the
    // section count, patched in by `_loadPathSectionsList` once they arrive.
    let progressChip = null;
    if (isCompleted) {
      // Both partners can have finished the path itself while the shared
      // guide's disclosure-set confirm is still outstanding on one side
      // (`sharingConfirmOwedByYou`/`sharingConfirmOwedByPartner`, viewer-
      // master-gated, absent when not applicable). Owed-by-you is the
      // viewer's OWN outstanding, actionable state — it forces the viewer's
      // line into the incomplete (×, warning-tone) treatment, mirroring the
      // partner-completion-asymmetry idiom below. Owed-by-partner is someone
      // else's private, already-finished-the-path choice — A3's
      // neutral-informational ceiling means it must never read as
      // actionable-red toward the viewer, so it renders through the neutral
      // (`neutralMessage`) tone instead: no ×, no warning color.
      const confirmOwedByYou = Boolean(path.sharingConfirmOwedByYou);
      const confirmOwedByPartner = Boolean(path.sharingConfirmOwedByPartner);
      const partnerPathComplete = !!partnerProgress && this.isParticipantComplete(partnerProgress);
      const currentLine = this.renderParticipantCompletionLine(currentUserProgress, {
        fallbackName: this.getCurrentUserDisplayName(),
        incompleteMessage: confirmOwedByYou ? 'Waiting on you' : undefined,
        forceIncomplete: confirmOwedByYou,
        className: 'path-detail__progress-info-line--current'
      });
      // "Waiting on <name>" (×, warning-tone) only where the partner's
      // completion blocks a shared artifact — i.e. the partner has genuinely
      // not finished the path; on individual-only paths the default neutral
      // "<name> has not completed this path yet" line renders instead. A
      // confirm owed by an already-finished partner takes the separate
      // neutral tone above, never this warning-toned branch.
      const partnerLine = partnerProgress
        ? this.renderParticipantCompletionLine(partnerProgress, {
            fallbackName: 'Your partner',
            incompleteMessage: Boolean(path.partnerCompletionBlocks)
              ? `Waiting on ${this.getProgressName(partnerProgress, 'your partner')}`
              : undefined,
            neutralMessage: (confirmOwedByPartner && partnerPathComplete)
              ? `Waiting on ${this.getProgressName(partnerProgress, 'your partner')}`
              : undefined,
            className: 'path-detail__progress-info-line--partner'
          })
        : '';
      progressChip = '100%';
      progressBlockHTML = `
        <div class="path-detail__progress-section">
          <div class="path-detail__progress-info">
            <div class="path-detail__progress-participants">
              ${currentLine}
              ${partnerLine}
            </div>
          </div>
        </div>
      `;
    } else if (isInProgress) {
      const percent = currentUserProgress.percentComplete || 0;
      progressChip = `${percent}%`;
      const barWidth = this.progressToBarWidth(percent);
      // Same figure as the nav card: sections not fully resolved
      // (questionnaire + conversation). The drawer must never disagree with
      // the card it opened from. Minutes cover questionnaires + pending L2
      // conversations; the zero-drop clause survives as defense only.
      const remainingQ = Number.isFinite(currentUserProgress.sectionsRemaining)
        ? currentUserProgress.sectionsRemaining
        : Math.max(0, (currentUserProgress.questionnairesTotal || 0) - (currentUserProgress.questionnairesCompleted || 0));
      const remainingMins = path.minutesRemaining || 0;
      const stepLabel = remainingQ === 1 ? 'section' : 'sections';
      const minsClause = remainingMins > 0 ? `, ${remainingMins} minutes` : '';
      const infoHTML = remainingQ > 0
        ? `<div class="path-detail__progress-info-line">${remainingQ} ${stepLabel}${minsClause} remaining</div>`
        : '';
      // A partner who already finished, stacked BELOW the remaining-work line
      // in the same completion idiom the completed branch uses — the nav card
      // that opened this panel already says "<name> finished", and the drawer
      // must not know less than its card. Two constraints are load-bearing:
      //
      // - **Positive case only.** No line for a partner still mid-path: "× X
      //   has not completed this path yet" is noise while the VIEWER is also
      //   unfinished, and even on a `partnerCompletionBlocks` path nothing is
      //   waiting on them yet — you cannot be blocked before you finish. This
      //   line stays purely informational, the same neutral ceiling the
      //   completed branch's `confirmOwedByPartner` case observes.
      // - **Partner only.** The bar and the remaining-work line ARE the
      //   viewer's own participant line here; a second one would restate them.
      //
      // Stacked, not right-aligned beside the remaining-work line: a
      // two-column row reads as a scoreboard (your work left vs their check),
      // which is the wrong frame for a couples product.
      const partnerFinishedHTML = (partnerProgress && this.isParticipantComplete(partnerProgress))
        ? `<div class="path-detail__progress-info">
            <div class="path-detail__progress-participants">
              ${this.renderParticipantCompletionLine(partnerProgress, {
                fallbackName: 'Your partner',
                className: 'path-detail__progress-info-line--partner'
              })}
            </div>
          </div>`
        : '';
      progressBlockHTML = `
        <div class="path-detail__progress-section">
          <div class="path-detail__progress-row">
            <div class="understanding__bar-track" style="--bar-width:${barWidth}%">
              <div class="understanding__bar-fill"></div>
            </div>
            <span class="path-detail__progress-percent">${percent}%</span>
          </div>
          ${infoHTML}
          ${partnerFinishedHTML}
        </div>
      `;
    }

    const detailContent = document.createElement('div');
    detailContent.className = 'discovery-detail-content content-width';
    detailContent.innerHTML = `
      <header class="path-detail__head">
        <h1 class="sd-title">${this.escapeHtml(path?.name || 'Path Detail')}</h1>
        ${description ? `<p class="sd-subtitle">${this.escapeHtml(description)}</p>` : ''}
        ${metaPillsHTML}
      </header>

      ${workable ? `
        ${whatToExpectHTML}

        ${renderDisclosure(
          PATH_PROGRESS_ZONE,
          (isInProgress || isCompleted) ? 'Current progress' : "Sections we'll cover",
          `${progressBlockHTML}
          <div data-path-sections-mount>
            <p class="section-checklist__empty">Loading sections…</p>
          </div>`,
          { expanded: true, count: progressChip }
        )}
      ` : `
        ${this._renderPathExpectBlock(path, imageHTML)}
        ${this._renderPathAvailabilityMarker(path)}
      `}
    `;

    // Zone headers toggle their body via the shared primitive — delegated on
    // `detailContent`, so it still reaches the header after the sections list
    // hydrates into the mount inside its body.
    wireDisclosures(detailContent);

    this._detailContent = detailContent;
    this.currentSelection = { kind: 'path', id: path.id };
    this._panelOpen = true;
    this._refreshActiveStates();

    // `RightPanelManager`'s primary-button contract is `{label, onClick} | null`
    // — there is no disabled state, and it is not extended for this. The slot
    // answers "what do I do with this path?" for every kind: work it, or —
    // PATH ACCESS — buy it. Only `premium_required` has somewhere to go
    // (`goToUpgrade`, surface `pathDetail`); an unbuilt or phase-locked path
    // has nothing to sell, so it gets no button, exactly as a completed one
    // already does, and the availability marker in the body says why.
    // Detail pages carry no in-body CTA (detail-page-style-guide §4), which
    // is why the offer lives up here and not on the marker.
    let primaryButton = null;
    if (workable && !isCompleted) {
      primaryButton = { label: ctaLabel, onClick: () => this._workOnPath(path) };
    } else if (premiumRequired) {
      primaryButton = { label: 'Upgrade to Premium', onClick: () => goToUpgrade(eventBus, 'pathDetail') };
    }

    eventBus.emit('right-panel:open', {
      title: '',
      titleAlign: 'left',
      primaryButton,
      content: detailContent,
      contentType: 'discovery-path'
    });

    requestAnimationFrame(() => {
      const guideCard = detailContent.querySelector('[data-path-guide-id]');
      if (guideCard) {
        const guideId = guideCard.dataset.pathGuideId;
        const handleGuideOpen = () => this._drillToGuideFromPath(guideId);
        guideCard.addEventListener('click', handleGuideOpen);
        guideCard.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleGuideOpen();
          }
        });
      }

      // PATH ACCESS — the fetch is gated, not just the render. Unlike
      // `POST /api/discovery/paths/:pathId/next`, `GET /api/discovery/paths/:pathId`
      // carries no `resolvePathAccess` guard (backend/src/routes/discoveryPaths.js),
      // so calling it for a `premium_required` path would hand the panel the
      // full section checklist — every questionnaire, its count and duration —
      // for content the user has not paid for. That is latent only because
      // BASIC_PATH_IDS === BUILT_PATH_IDS today; it goes live the day the first
      // paid path ships.
      if (workable) this._loadPathSectionsList(path.id, detailContent);

      detailContent.scrollTop = 0;
    });
  }

  /**
   * Enter DISCOVERY on a path — the single work-entry action, shared by the
   * detail panel's primary button and the Paths hero pill. Reentrancy-guarded
   * (`_workingOnPath`) so a double-click cannot fire two hand-offs.
   *
   * The guard is released in `deactivate()` (the success path — the section
   * unmounts as chat takes over) and again whenever the tab rebinds, which is
   * what keeps a hand-off that never completed from latching the button dead.
   * A navigation can be intercepted rather than followed — a premium gate, say
   * — leaving the section mounted; the Progress tab avoids this by scoping its
   * `workingOnNext` to each `bindProgressEvents` call, and rebind-release is
   * the same guarantee for an instance flag two surfaces share.
   *
   * This is where path access is actually enforced: routing to a path is
   * display-only, and no surface offers this action for a path
   * `_isPathWorkable` rejects.
   *
   * @param {Object} path - Path listing entry
   * @private
   */
  _workOnPath(path) {
    if (!path || this._workingOnPath) return;
    this._workingOnPath = true;
    eventBus.emit('navigate', {
      page: 'chat',
      mode: 'DISCOVERY',
      pathId: path.id,
      // Thread the path metadata we already have in hand so the dynamic header
      // renders the real path name on the first frame (no "Discovery" flash while
      // _runPathLoop's getPathById fetch resolves). _runPathLoop backstops this
      // for entries that arrive without metadata.
      pathMetadata: {
        id: path.id,
        name: path.name,
        phase: path.phase,
        description: path.description || '',
        cardContent: path.cardContent
      }
    });
  }

  /**
   * The inert availability marker a non-workable path's panel carries where a
   * workable path's progress block and sections list would be. It CLOSES the
   * panel rather than opening it: as a filled plate above the content it read
   * as an alert bar interrupting the header, when what it reports is a quiet
   * "not yet" that belongs after the pitch. The nav row's status chip already
   * said so before the panel opened.
   *
   * It is a note, never the action: a Premium path's offer is the panel
   * header's primary button (`showDetailView`), and a plate that also looked
   * clickable would compete with it. What the plate adds for a Premium path is
   * a second, quieter line saying what Premium opens — the same forward-looking
   * "every discovery path" the pricing page promises, so the two never drift.
   *
   * The three withheld kinds mean different things and
   * must not be collapsed into one line; the copy restates what the sheet said
   * about the same path in the singular — the matching group label
   * (`renderPremiumPathsNotice` / `renderLockedPathsNotice`), or for an unbuilt
   * path its row's own `Coming soon` status chip:
   *
   *   - `not_built`         → never unlocks; it does not exist yet
   *   - `premium_required`  → unlocks by subscribing, actionable today
   *   - phase-locked        → unlocks by finishing Starting Your Journey
   *
   * @param {Object} path - Path listing entry
   * @returns {string} HTML string, or '' for a workable path
   * @private
   */
  _renderPathAvailabilityMarker(path) {
    let label;
    let note = '';
    if (path?.accessReason === 'not_built') {
      label = 'Coming soon';
    } else if (path?.accessReason === 'premium_required') {
      label = 'Available with Premium';
      note = 'Premium opens every discovery path, full guides and unlimited messages.';
    } else if ((path?.phaseAccess ?? false) === false) {
      label = 'Unlocks after Starting Your Journey';
    } else {
      return '';
    }

    const noteHtml = note
      ? `<span class="path-detail__availability-note">${this.escapeHtml(note)}</span>`
      : '';
    return `
      <p class="path-detail__availability" role="note"><span class="path-detail__availability-state">${this.escapeHtml(label)}</span>${noteHtml}</p>
    `;
  }

  /**
   * The "What to expect" shell both path-detail branches render: section
   * header, then the path artwork leading a body. The artwork appears exactly
   * once on any panel — a completed path shows it on the generated-guide card
   * instead of here — and never as a floating header ornament.
   *
   * @param {string} bodyHTML - The block's content, right of the artwork
   * @param {string} imageHTML - Pre-escaped `<img>`, or '' for an imageless path
   * @returns {string} HTML string
   * @private
   */
  _renderExpectBlock(bodyHTML, imageHTML) {
    return `
      <div class="sd-section-header">What to expect</div>
      <div class="path-detail__expect">
        ${imageHTML ? `<div class="path-disc path-detail__image">${imageHTML}</div>` : ''}
        ${bodyHTML}
      </div>
    `;
  }

  /**
   * A non-workable path's "What to expect" — the same shell a workable path
   * gets, so a withheld path's panel is not visibly a different kind of page,
   * but carrying `cardContent.bulletPoints` where a workable path carries
   * `path.description` prose.
   *
   * **The bullets, not the prose, on purpose.** For every unbuilt path the two
   * authored strings are paraphrases of each other in different voices —
   * `cardContent.description` (second person, already the header subtitle) and
   * `path.description` (third-person clinical). Rendering the prose here would
   * restate the subtitle the reader just read and drop the only concrete
   * content on a page that has no progress bar, no section list and no CTA.
   * The built paths earn their prose (Continuing Your Journey's names ten
   * constructs and the instruments behind them); these do not. A reader only
   * ever sees one variant, so the header carries both bodies honestly.
   *
   * The question count, duration and guide-generation facts stay out of here —
   * they are already in the header's meta pills.
   *
   * @param {Object} path - Path listing entry
   * @param {string} imageHTML - Pre-escaped `<img>`, or '' for an imageless path
   * @returns {string} HTML string, or '' when the path declares no bullets
   * @private
   */
  _renderPathExpectBlock(path, imageHTML) {
    const bullets = path?.cardContent?.bulletPoints;
    if (!Array.isArray(bullets) || bullets.length === 0) return '';

    const items = bullets
      .map(b => `<li>${this.escapeHtml(String(b))}</li>`)
      .join('');

    return this._renderExpectBlock(`<ul class="path-detail__covers">${items}</ul>`, imageHTML);
  }

  /**
   * Fetch the path's questionnaire list and populate the timeline mount
   * inside the detail panel. Bails out if the panel has been swapped to
   * a different path while the request was in flight.
   * @private
   * @param {string} pathId
   * @param {HTMLElement} detailContent
   */
  async _loadPathSectionsList(pathId, detailContent) {
    const mount = detailContent.querySelector('[data-path-sections-mount]');
    if (!mount) return;

    let pathDetail;
    try {
      const response = await this.discoveryAPI.getPathById(pathId);
      pathDetail = response?.data ?? response;
    } catch (err) {
      console.error('[DiscoverySection] Failed to load path detail:', err);
      if (this.currentSelection?.kind === 'path' && this.currentSelection.id === pathId) {
        mount.innerHTML = `<p class="section-checklist__empty">Couldn't load sections.</p>`;
      }
      return;
    }

    if (this.currentSelection?.kind !== 'path' || this.currentSelection.id !== pathId) {
      return;
    }

    const entries = Array.isArray(pathDetail?.questionnaires) ? pathDetail.questionnaires : [];
    if (entries.length === 0) {
      mount.innerHTML = `<p class="section-checklist__empty">No sections yet.</p>`;
      return;
    }

    const partnerName = this.partnerData?.firstName || 'your partner';
    const items = entries.map(entry => {
      const q = entry.questionnaire || entry;
      return {
        key: q.code || q.id || undefined,
        name: q.displayName || q.name || q.code || q.id || 'Section',
        description: (q.description || '').replace(/\{\{PARTNER_NAME\}\}/g, partnerName),
        status: entry.status || 'not_started'
      };
    });

    // Shared with the topic-detail panel (components/sectionChecklist.js); the
    // path surface keeps the default green marker palette.
    mount.innerHTML = renderSectionChecklist(items);
    bindSectionChecklist(mount);

    // A path with no progress figure gets its zone chip from the section count
    // instead — the only at-a-glance size a not-yet-started path has, and not
    // known until now. Progressed paths already carry their percentage.
    this._fillPathProgressChip(detailContent, items.length);
  }

  /**
   * Fill the path progress zone's empty chip with the section count. A no-op
   * when the header already carries a chip (a progressed path's percentage),
   * so the figure the zone renders with always wins.
   * @private
   * @param {HTMLElement} detailContent - Path detail panel content element
   * @param {number} count - Number of sections in the path
   */
  _fillPathProgressChip(detailContent, count) {
    const toggle = detailContent.querySelector(`[data-disclosure-toggle="${PATH_PROGRESS_ZONE}"]`);
    if (!toggle || toggle.querySelector('.sd-zone-count')) return;
    const chip = document.createElement('span');
    chip.className = 'sd-zone-count';
    chip.textContent = String(count);
    toggle.appendChild(chip);
  }


  /**
   * Render the guide nav card displayed in the path detail's "Generated Guide"
   * section.
   *
   * Same artifact-card format as `/library`'s guide rows
   * (`InsightsSection.renderGuideCard`, `.artifact--guide-card` in
   * guide-card.css): tilted document tile carrying the path artwork, guide
   * title + tagline (both from `path.guideStatus`, resolved server-side from
   * the guide definition's per-mode templates so the two surfaces share their
   * copy), and the date ↔ chevron hover swap in the meta slot. Unlike the
   * library rows it takes no kebab and no star — guide management lives on the
   * guide's own surfaces. The card only renders for READY/STALE guides (see
   * `showDetailView`).
   * @private
   * @param {Object} path
   * @returns {string} HTML string
   */
  _renderPathGuideCard(path) {
    const guideId = path.guideDefinitionId;
    const guideStatus = path.guideStatus || {};
    const title = guideStatus.title || path.name || 'Generated guide';
    const tagline = guideStatus.tagline || '';

    const image = path.cardContent?.image;
    const icon = image
      ? `<div class="artifact__icon artifact__icon--image"><img class="artifact__icon-img" alt="" src="assets/images/paths/${encodeURIComponent(image)}"></div>`
      : `<div class="artifact__icon"><svg width="25" height="28" aria-hidden="true"><use href="#icon-guide"/></svg></div>`;

    const date = guideStatus.generatedAt
      ? `<span class="note-row__date">${this.escapeHtml(formatRelativeDate(guideStatus.generatedAt))}</span>`
      : '';

    return `
      <div class="artifact artifact--clickable artifact--guide-card guide-nav-card note-row path-detail__guide-card"
           data-path-guide-id="${this.escapeHtml(guideId)}"
           role="button"
           tabindex="0">
        <div class="artifact__inner">
          <div class="artifact__left">
            <div class="artifact__icon-wrapper">${icon}</div>
            <div class="artifact__content">
              <div class="artifact__title-row">
                <span class="artifact__title">${this.escapeHtml(title)}</span>
              </div>
              <div class="artifact__subtitle">${this.escapeHtml(tagline)}</div>
            </div>
          </div>
          <span class="note-row__meta">
            ${date}
            <span class="session-card__chevron note-row__chevron" aria-hidden="true">
              <svg width="19" height="19" viewBox="0 0 16 16">
                <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              </svg>
            </span>
          </span>
        </div>
      </div>
    `;
  }

  /**
   * Drill from the path detail panel into the guide detail view. Uses the
   * RightPanelManager's `right-panel:drill` so the back arrow auto-wires to
   * pop() and restores the path detail panel verbatim.
   * @private
   * @param {string} guideId - Guide definition ID
   */
  async _drillToGuideFromPath(guideId) {
    if (!guideId) return;

    this.selectedGuide = null;
    this._markGuideViewed(guideId);
    const content = document.createElement('div');
    content.innerHTML = this.renderGuideDetailPanel();

    eventBus.emit('right-panel:drill', {
      title: '',
      titleAlign: 'left',
      optionsDropdown: this._buildGuideOptions(guideId),
      headerMenu: buildGuideContentsMenu(this.selectedGuide),
      content,
      contentType: 'library-guide',
      // Guide-contents-rail plan D10/D12: nudge wider on open, mirroring
      // showGuideDetailView.
      preferredWidth: 880,
      // See showGuideDetailView: one owner for the rail's spy, keyed on this
      // node so a late dispose cannot tear down the next reader's.
      onDispose: () => this._disposeContentsSpyFor(content)
    });

    this._armContentsSpy(content);

    try {
      const guide = await API.getGuide(guideId);
      if (guide) {
        this.selectedGuide = guide;
        content.innerHTML = this.renderGuideDetailPanel();
        this._syncGuideOptions(guideId);
        // The replacement built the real rail; arm the spy over it.
        this._armContentsSpy(content);
      }
    } catch (error) {
      console.error('[DiscoverySection] Error loading guide:', error);
    }
  }

  /**
   * Persist and locally clear a viewed guide badge shared with Insights.
   * @private
   * @param {string} guideId - Guide definition ID
   */
  async _markGuideViewed(guideId) {
    if (!guideId) return;
    const updated = await API.markGuideViewed(guideId);
    if (!updated) return;
    emitContentViewed('guide', guideId, true);
  }

  /**
   * Show topic detail view in the slide panel.
   * Looks up the topic in this.progressTopics, renders via TopicDetailView,
   * and wires the "Start/Continue Exploring This Topic" CTA.
   *
   * @param {string} topicId - Topic registry ID (e.g. 'communication')
   * @param {Object} [_options] - Reserved for `{ skipUrlPush }`; the panel
   *   itself does not push URL state, but the flag exists for API symmetry.
   */
  showTopicDetailView(topicId, _options = {}) {
    const topic = this.progressTopics.find(t => t.id === topicId);
    if (!topic) {
      console.warn('[DiscoverySection] Topic not found:', topicId);
      return;
    }

    // Shared content build (markup + disclosure/thread wiring + hydration) —
    // the understanding-map panel's row drill renders the identical detail
    // through the same helper. Thread cards drill into the shared thread
    // detail view, swapping the header and popping back to this one.
    const { content: detailContent, hydrate } = buildTopicDetailContent(topic, {
      onThreadClick: (thread) => {
        const payload = this._buildThreadDetailPayload(thread);
        const panel = window.app?.modules?.rightPanelManager;
        if (panel?.canDrill?.()) {
          eventBus.emit('right-panel:drill', payload);
        } else {
          eventBus.emit('right-panel:open', payload);
        }
      }
    });

    this._detailContent = detailContent;
    this.currentSelection = { kind: 'topic', id: topicId };
    this._panelOpen = true;
    this._refreshActiveStates();

    // Guards against double-fire while enterTopicDiscovery() is in flight.
    let workingOnTopic = false;

    // "Explore this topic" is the panel's header primary button.
    eventBus.emit('right-panel:open', {
      title: '',
      titleAlign: 'left',
      primaryButton: {
        label: 'Explore this topic',
        onClick: async () => {
          if (workingOnTopic) return;
          workingOnTopic = true;
          // Shared two-step (persist selection → navigate DISCOVERY).
          await enterTopicDiscovery(topicId, { discoveryAPI: this.discoveryAPI });
        }
      },
      content: detailContent,
      contentType: 'discovery-topic'
    });

    // Fire-and-forget: the header/overview/sections above are already
    // painted; a response landing after the selection moved on is dropped.
    hydrate({ stillCurrent: () => this._isSelected('topic', topicId) });
  }

  /**
   * Close the detail right panel. Used by in-body "back" buttons (e.g. the
   * mobile "All Paths" / "All threads" buttons) so they have the same effect
   * as clicking the panel's own close button.
   */
  handleBackClick() {
    eventBus.emit('right-panel:close');
  }

  // ===== QUESTIONS TAB METHODS =====

  /**
   * Load explore questions from backend
   */
  async loadQuestionsData() {
    this.isLoadingQuestions = true;
    this.questionsLoadError = null;

    try {
      if (this.questionsFilter === 'dismissed') {
        // Archived tab shows both exit populations: user dismissals
        // ("you set this aside") and system archivals ("this went quiet").
        const [dismissed, archived] = await Promise.all([
          API.getOpenThreads({ status: 'dismissed', limit: 100 }),
          API.getOpenThreads({ status: 'archived', limit: 100 })
        ]);
        if (Array.isArray(dismissed) && Array.isArray(archived)) {
          const byId = new Map();
          [...dismissed, ...archived].forEach((t) => byId.set(t.id, t));
          this.openThreads = [...byId.values()];
        } else {
          this.openThreads = [];
          this.questionsLoadError = 'Failed to load questions';
        }
      } else {
        const data = await API.getOpenThreads({
          status: this.questionsFilter,
          limit: 100
        });

        if (Array.isArray(data)) {
          this.openThreads = data;
        } else {
          this.openThreads = [];
          this.questionsLoadError = 'Failed to load questions';
        }
      }
    } catch (error) {
      console.error('[DiscoverySection] Error loading questions:', error);
      this.openThreads = [];
      this.questionsLoadError = 'Failed to load questions';
    } finally {
      this.isLoadingQuestions = false;
    }

    // Re-resolve a URL ?thread= that may have been deep-linked before
    // threads finished loading.
    this._resolvePendingDetailFromURL();
  }

  /**
   * Render questions tab content shell (filter bar mount + list mount).
   * The bar is owned by ResultsFilterBar (mounted in _mountQuestionsFilterBar).
   * The list region holds loading / error / empty / cards depending on state.
   * @returns {string} HTML string
   */
  renderQuestionsContent() {
    return `
      <div class="share-tab-content" data-tab-content="threads">
        <div data-filter-bar-mount="threads" data-sticky-slice></div>
        <div data-questions-list>
          ${this.renderQuestionsList()}
        </div>
      </div>
    `;
  }

  /**
   * Render the questions-list region only (without the filter bar).
   * @returns {string} HTML string
   */
  renderQuestionsList() {
    if (this.isLoadingQuestions) {
      return renderEmptyState({ title: 'Loading questions…' });
    }

    if (this.questionsLoadError) {
      return renderEmptyState({ title: this.questionsLoadError });
    }

    const notice = this._renderThreadActionNotice();
    const visible = this._visibleThreads();

    if (visible.length === 0) {
      if (this.threadStarredFilter) {
        return notice + renderEmptyState({
          title: 'No starred threads',
          description: 'Star a thread to keep it handy here.',
          filtered: !this.selectMode
        });
      }
      const emptyText = this.questionsFilter === 'addressed'
        ? 'No explored threads yet'
        : this.questionsFilter === 'dismissed'
          ? 'No archived threads yet'
          : 'No open threads yet';

      return notice + renderEmptyState({
        title: emptyText,
        description: 'Questions appear here after your therapy sessions.'
      });
    }

    const sortedThreads = this.applyThreadSort(visible);
    const containerCls = this.selectMode ? ' content-rows--select' : '';

    return `${notice}
      <div class="questions-container${containerCls}">
        ${sortedThreads.map(t => this.renderThreadCard(t, {
          selectMode: this.selectMode,
          selected: this.selectedThreadIds.has(t.id)
        })).join('')}
      </div>
    `;
  }

  /**
   * Render the bulk-action failure notice (empty string when clear). Lives
   * inside the list region so it clears/re-renders with the cards. Mirrors
   * SessionsSection.renderSourceErrorsNotice.
   * @returns {string} HTML string
   */
  _renderThreadActionNotice() {
    if (!this.threadActionNotice) return '';
    return `<div class="session-cards-notice">${this.escapeHtml(this.threadActionNotice)}</div>`;
  }

  /**
   * Mount the shared ResultsFilterBar into the questions tab.
   */
  _mountQuestionsFilterBar() {
    const mount = this.container.querySelector('[data-filter-bar-mount="threads"]');
    if (!mount) return;
    if (this.questionsFilterBar) {
      this.questionsFilterBar.destroy();
      this.questionsFilterBar = null;
    }
    // Select mode swaps the whole bar for the bulk toolbar.
    if (this.selectMode) {
      this._renderThreadSelectToolbar(mount);
      return;
    }
    this.questionsFilterBar = new ResultsFilterBar({
      container: mount,
      itemLabel: { singular: 'Thread', plural: 'Threads' },
      fixedItemLabel: { singular: 'Thread', plural: 'Threads' },
      count: this._visibleThreads().length,
      filters: [
        {
          id: 'status',
          label: 'Show',
          defaultValue: this.questionsFilter,
          options: [
            { value: 'unaddressed', label: 'Open', itemLabel: { singular: 'Open Thread', plural: 'Open Threads' } },
            { value: 'addressed', label: 'Explored', itemLabel: { singular: 'Explored Thread', plural: 'Explored Threads' } },
            { value: 'dismissed', label: 'Archived', itemLabel: { singular: 'Archived Thread', plural: 'Archived Threads' } }
          ]
        }
      ],
      sort: {
        defaultValue: this.questionsSortOrder,
        options: [
          { value: 'newest', label: 'Newest' },
          { value: 'oldest', label: 'Oldest' }
        ]
      },
      actions: [
        { id: 'starred', label: 'Starred only', icon: 'star', pressed: this.threadStarredFilter, onClick: () => this._toggleThreadStarredFilter() },
        { id: 'select', label: 'Select', icon: 'select', onClick: () => this._enterThreadSelectMode() }
      ],
      onChange: (state) => this._handleQuestionsFilterChange(state)
    });
  }

  /**
   * Render the select-mode bulk toolbar into the bar mount: "N selected" left;
   * Star/Unstar, Archive/Unarchive, Delete, Cancel right. Bulk buttons disable
   * at zero selection or while a batch is in flight. Direct port of
   * SessionsSection._renderSelectToolbar over `selectedThreadIds`.
   * @param {HTMLElement} [mount] - Bar mount (looked up when omitted)
   */
  _renderThreadSelectToolbar(mount = null) {
    const el = mount || this.container.querySelector('[data-filter-bar-mount="threads"]');
    if (!el) return;
    const n = this.selectedThreadIds.size;
    const disabled = n === 0 || this._threadActionInFlight;
    const selected = this._selectedThreads();
    const allStarred = n > 0 && selected.every((t) => t.starredAt);
    const allArchived = n > 0 && selected.every((t) => t.dismissedAt);
    mountSelectToolbar(el, {
      count: n,
      actions: [
        { action: 'select-star', label: allStarred ? 'Unstar' : 'Star', icon: allStarred ? 'star' : 'star-outline', disabled },
        { action: 'select-archive', label: allArchived ? 'Unarchive' : 'Archive', icon: allArchived ? 'unarchive' : 'archive', disabled },
        { action: 'select-delete', label: 'Delete', icon: 'trash', disabled },
        { action: 'select-cancel', label: 'Cancel', icon: 'close' }
      ]
    });
  }

  /**
   * Threads currently visible in the list = the loaded status population,
   * narrowed by the client-side "Starred only" filter when active.
   * @returns {Array} Filtered thread records
   */
  _visibleThreads() {
    return this.threadStarredFilter
      ? this.openThreads.filter((t) => t.starredAt)
      : this.openThreads;
  }

  /**
   * The currently-selected thread records (resolved from the loaded list).
   * @returns {Array}
   */
  _selectedThreads() {
    return this.openThreads.filter((t) => this.selectedThreadIds.has(t.id));
  }

  /**
   * True if a thread belongs in the currently-selected status tab. Hoisted from
   * showThreadDetailView so bulk archive/unarchive can reuse it for list
   * membership sync. The Archived tab carries both user dismissals and system
   * archivals.
   * @param {Object} question - Open-thread record
   * @returns {boolean}
   */
  threadMatchesFilter(question) {
    if (this.questionsFilter === 'dismissed') return !!question.dismissedAt || !!question.archivedAt;
    if (this.questionsFilter === 'addressed') return !!question.addressedAt && !question.dismissedAt;
    return !question.addressedAt && !question.dismissedAt && !question.archivedAt;
  }

  /**
   * Toggle the client-side "Starred only" filter and re-render the list + bar.
   */
  _toggleThreadStarredFilter() {
    this.threadStarredFilter = !this.threadStarredFilter;
    // Update the LIVE bar rather than remounting it: the toggle lives inside
    // the filter menu now, so destroying and rebuilding the bar here would
    // close the menu the user just clicked in.
    this.questionsFilterBar?.setActionPressed('starred', this.threadStarredFilter);
    this._refreshQuestionsList();
  }

  /**
   * Handle filter/sort change from the questions bar.
   */
  async _handleQuestionsFilterChange(state) {
    const newStatus = state.filters.status;
    // Section-owned toggle: a bar-side reset reaches us only via the payload.
    const newStarred = Boolean(state.actions?.starred);
    const statusChanged = newStatus !== this.questionsFilter;
    const sortChanged = state.sort !== this.questionsSortOrder;
    const starredChanged = newStarred !== this.threadStarredFilter;

    this.questionsFilter = newStatus;
    this.questionsSortOrder = state.sort;
    this.threadStarredFilter = newStarred;

    if (statusChanged) {
      await this.loadQuestionsData();
      // A status change refetches; the tab may have been torn down meanwhile.
      if (!this.isActive) return;
    }

    if (statusChanged || sortChanged || starredChanged) {
      this._refreshQuestionsList();
    }
  }

  /**
   * Re-render only the questions list region; refresh count on the bar.
   */
  _refreshQuestionsList() {
    const listEl = this.container.querySelector('[data-questions-list]');
    if (listEl) {
      listEl.innerHTML = this.renderQuestionsList();
    }
    if (this.questionsFilterBar) {
      this.questionsFilterBar.setCount(this._visibleThreads().length);
    }
  }

  /**
   * Apply current sort order to flat thread list (by session.endedAt)
   * @param {Array} threads - Flat array of explore questions
   * @returns {Array} Sorted copy
   */
  applyThreadSort(threads) {
    const dir = this.questionsSortOrder === 'oldest' ? 1 : -1;
    return [...threads].sort((a, b) => {
      const aDate = new Date(a.session?.endedAt || 0).getTime();
      const bDate = new Date(b.session?.endedAt || 0).getTime();
      return dir * (aDate - bDate);
    });
  }


  /**
   * Right-rail at-rest content for a thread card (swaps to the chevron on
   * hover via the `.note-row` contract). Explored → "Completed on <date>"
   * with a check; archived → the exit label; open → the source session date.
   * @param {Object} question - Open-thread record
   * @returns {string} HTML string
   * @private
   */
  _threadCardMeta(question) {
    const isExplored = !!question.addressedAt;
    if (isExplored) {
      const completedDate = new Date(question.addressedAt)
        .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      return `<span class="note-row__date thread-card__completed">
          <svg class="thread-card__completed-icon" width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M3.5 8.2L6.5 11.2L12.5 4.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          </svg>
          ${this.escapeHtml(`Completed on ${completedDate}`)}
        </span>`;
    }
    // Archived-tab populations carry distinct labels: user dismissal vs.
    // system archival (expiry / agent review).
    const exitLabel = question.dismissedAt
      ? 'You set this aside'
      : question.archivedAt
        ? 'This went quiet'
        : '';
    if (exitLabel) {
      return `<span class="note-row__date thread-card__exit-label">${this.escapeHtml(exitLabel)}</span>`;
    }
    // Open thread: show when it last surfaced (source session date, else opened).
    const dateSource = question.session?.endedAt || question.createdAt;
    if (!dateSource) return '';
    const shortDate = new Date(dateSource)
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `<span class="note-row__date">${this.escapeHtml(shortDate)}</span>`;
  }

  /**
   * Render a single thread as its own two-row nav card (`.note-row` contract):
   * headline (row 1) + one-line context snippet (row 2); at-rest date / status
   * that swaps to the chevron on hover.
   * @param {Object} question - Open-thread record
   * @param {Object} [opts]
   * @param {boolean} [opts.selectMode] - Render the select checkbox slot instead of the plain card
   * @param {boolean} [opts.selected] - Whether this card is selected
   * @returns {string} HTML string
   */
  renderThreadCard(question, { selectMode = false, selected = false } = {}) {
    const isOpenInPanel = !selectMode && this._isSelected('thread', question.id);
    const activeCls = isOpenInPanel ? ' session-card--active' : '';
    const selectedCls = selectMode && selected ? ' session-card--active note-row--selected' : '';
    // Only the 3-column checkbox layout needs the --iconed modifier; the plain
    // (no-slot) thread card keeps the base 2-column grid.
    const iconedCls = selectMode ? ' session-card--iconed' : '';
    const titleText = threadTitleText(question);
    const snippet = question.context || question.observation?.title || '';
    const snippetHtml = snippet
      ? `<p class="session-card__summary nav-card__snippet thread-card__snippet">${this.escapeHtml(snippet)}</p>`
      : '';
    const star = question.starredAt
      ? `<span class="note-row__star" aria-label="Starred">${STAR_SVG}</span>`
      : '';
    const slot = selectMode
      ? `<span class="session-card__slot"><input type="checkbox" class="note-row__checkbox" tabindex="-1" aria-hidden="true"${selected ? ' checked' : ''}></span>`
      : '';
    const options = selectMode ? '' : this.renderThreadRowOptions(question);

    return `
      <div class="card session-card nav-card thread-card note-row${iconedCls}${activeCls}${selectedCls}"
           data-question-id="${question.id}"
           role="button"
           tabindex="0"
           ${selectMode ? `aria-pressed="${selected}"` : `aria-expanded="${isOpenInPanel}"`}>
        ${slot}
        <div class="session-card__content">
          <div class="session-card__title-row">
            ${star}
            <h4 class="session-card__title nav-card__title thread-card__title">${this.escapeHtml(titleText)}</h4>
          </div>
          ${snippetHtml}
        </div>
        <span class="note-row__meta">
          ${this._threadCardMeta(question)}
          ${options}
          <span class="session-card__chevron note-row__chevron" aria-hidden="true">
            <svg width="19" height="19" viewBox="0 0 16 16">
              <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </svg>
          </span>
        </span>
      </div>
    `;
  }

  /**
   * Render the hover-revealed kebab options button + its dropdown for one thread
   * card (ports SessionsSection.renderRowOptions). Items: Discuss (top,
   * auto-sorted by renderMenuItems), Star/Unstar, Archive/Unarchive (label from
   * `dismissedAt`), Delete (danger). Reuses the shared options-dropdown classes
   * (dropdowns.css).
   * @param {Object} question - Open-thread record
   * @returns {string} HTML string
   */
  renderThreadRowOptions(question) {
    const items = [
      { action: 'row-discuss', label: 'Discuss' },
      { action: 'row-star', label: question.starredAt ? 'Unstar' : 'Star', icon: question.starredAt ? 'star' : 'star-outline' },
      { action: 'row-archive', label: question.dismissedAt ? 'Unarchive' : 'Archive' },
      { action: 'row-delete', label: 'Delete', variant: 'danger' },
    ];
    return renderRowKebab({ items, dataAttrs: { 'data-thread-id': String(question.id) } });
  }

  /**
   * Attach event listeners for questions tab (mounts the shared filter bar
   * and wires up card interactions).
   */
  attachQuestionsEventListeners() {
    // Mount/refresh the shared filter+sort bar (status + sort dropdowns).
    this._mountQuestionsFilterBar();

    // Delegate from [data-questions-list], which survives filter re-renders
    // (the inner .questions-container is rebuilt each time the filter changes).
    const listEl = this.container.querySelector('[data-questions-list]');
    if (listEl) {
      listEl.addEventListener('click', (e) => this.handleQuestionsClick(e));
      listEl.addEventListener('keydown', (e) => this.handleQuestionsKeydown(e));
    }

    // Delegated data-action handler for the select-mode toolbar + per-row kebab.
    // CRITICAL: the toolbar lives in the filter-bar mount, OUTSIDE
    // [data-questions-list] — binding this on the list alone would leave the
    // toolbar's Star/Archive/Delete/Cancel buttons silently dead. Bind on the
    // section container; the stable arrow ref makes repeat attaches idempotent.
    this.container.addEventListener('click', this.handleThreadActionClick);

    // Right-click a thread card to open its kebab menu (same as the sidebar).
    // Stable arrow ref (class field) — attachQuestionsEventListeners() re-runs
    // on every threads-tab re-render, and addEventListener only dedupes
    // identical function references, so an inline closure here would
    // accumulate duplicate listeners (menu opens then instantly re-closes).
    this.container.addEventListener('contextmenu', this.handleThreadContextMenu);
  }

  /**
   * Delegated click handler for thread card body clicks: navigate (default) or
   * toggle selection (select mode). Kebab / menu / checkbox-action clicks are
   * owned by handleThreadActionClick, so they're skipped here.
   * @param {MouseEvent} e - Click event
   */
  handleQuestionsClick(e) {
    if (e.target.closest('[data-action]') || e.target.closest('.note-row__actions')) return;
    const card = e.target.closest('.thread-card[data-question-id]');
    if (!card) return;
    if (this.selectMode) {
      this._toggleThreadSelected(card.dataset.questionId);
    } else {
      this._navigateToThreadByQuestionId(card.dataset.questionId);
    }
  }

  /**
   * Keyboard activation for thread nav cards (Enter/Space). Toggles selection
   * in select mode, else navigates.
   * @param {KeyboardEvent} e - Keydown event
   */
  handleQuestionsKeydown(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    if (e.target.closest('[data-action]') || e.target.closest('.note-row__actions')) return;
    const card = e.target.closest('.thread-card[data-question-id]');
    if (!card) return;
    e.preventDefault();
    if (this.selectMode) {
      this._toggleThreadSelected(card.dataset.questionId);
    } else {
      this._navigateToThreadByQuestionId(card.dataset.questionId);
    }
  }

  /**
   * Delegated handler for the select-mode toolbar buttons + per-row kebab / menu
   * items. Bound once on the section container (stable arrow ref); reacts only
   * to its own action set, so other tabs' data-action buttons hit `default`.
   * @param {MouseEvent} event
   */
  /**
   * Right-click a thread card to open its kebab menu (same as the sidebar).
   * @param {MouseEvent} event
   */
  handleThreadContextMenu = (event) => {
    this._threadRowMenu.handleContextMenu(event);
  };

  handleThreadActionClick = (event) => {
    const target = event.target.closest('[data-action]');
    if (!target || !this.container.contains(target)) return;
    const action = target.getAttribute('data-action');
    const id = target.getAttribute('data-thread-id');

    switch (action) {
      case 'clear-filters':
        // Same action as the filter menu's "Reset filters" row — one code path.
        this.questionsFilterBar?.reset();
        break;
      case 'row-options':
        event.stopPropagation();
        this._threadRowMenu.toggle(target);
        break;
      case 'row-discuss':
        event.stopPropagation();
        this._threadRowMenu.close();
        if (id) this.handleQuestionClick(id);
        break;
      case 'row-star':
        event.stopPropagation();
        this._threadRowMenu.close();
        if (id) this._runThreadBulk('star', [id]);
        break;
      case 'row-archive':
        event.stopPropagation();
        this._threadRowMenu.close();
        if (id) this._runThreadBulk('archive', [id]);
        break;
      case 'row-delete':
        event.stopPropagation();
        this._threadRowMenu.close();
        if (id) this._runThreadBulk('delete', [id]);
        break;
      case 'select-star':
        this._runThreadBulk('star', [...this.selectedThreadIds]);
        break;
      case 'select-archive':
        this._runThreadBulk('archive', [...this.selectedThreadIds]);
        break;
      case 'select-delete':
        this._runThreadBulk('delete', [...this.selectedThreadIds]);
        break;
      case 'select-cancel':
        this._exitThreadSelectMode();
        break;
      default:
        break;
    }
  };

  // ===== SELECT MODE =====

  /** Enter multi-select mode: swap the bar for the toolbar, re-render rows. */
  _enterThreadSelectMode() {
    if (this.selectMode) return;
    this.selectMode = true;
    this.selectedThreadIds = new Set();
    this._threadRowMenu.close();
    this._mountQuestionsFilterBar();
    this._refreshQuestionsList();
  }

  /** Exit multi-select mode: clear selection, restore the filter bar. */
  _exitThreadSelectMode() {
    this.selectMode = false;
    this.selectedThreadIds = new Set();
    this._mountQuestionsFilterBar();
    this._refreshQuestionsList();
  }

  /**
   * Toggle one card in the selection and refresh toolbar + rows.
   * @param {string} id - OpenThread UUID
   */
  _toggleThreadSelected(id) {
    if (!id) return;
    if (this.selectedThreadIds.has(id)) this.selectedThreadIds.delete(id);
    else this.selectedThreadIds.add(id);
    this._renderThreadSelectToolbar();
    this._refreshQuestionsList();
  }

  // ===== BULK ACTION FAN-OUT =====

  /**
   * Run a bulk action (star | archive | delete) over a set of thread ids, via
   * the single bulk endpoint per action. Delete confirms first; star/archive
   * don't. Local state updates only on a success envelope (`result &&
   * result.success !== false`); on failure a notice renders in the list region.
   * Toggle direction (star/archive) is computed from the current selection.
   * @param {'star'|'archive'|'delete'} action
   * @param {string[]} ids - OpenThread UUIDs
   */
  async _runThreadBulk(action, ids) {
    if (this._threadActionInFlight || !Array.isArray(ids) || ids.length === 0) return;
    const idSet = new Set(ids);
    const targets = this.openThreads.filter(t => idSet.has(t.id));
    if (targets.length === 0) return;

    if (action === 'delete') {
      const n = targets.length;
      const confirmed = await modalService.confirm({
        title: `Delete ${n === 1 ? 'thread' : `${n} threads`}`,
        message: `${n === 1 ? 'This thread' : 'These threads'} will be removed. This can’t be undone.`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
        variant: 'danger'
      });
      if (!confirmed || !this.isActive) return;
    }

    this._threadActionInFlight = true;
    if (this.selectMode) this._renderThreadSelectToolbar();
    this.threadActionNotice = '';

    const panelThreadId = this.currentSelection?.kind === 'thread' ? this.currentSelection.id : null;
    let failed = false;
    // Direction applied by an archive run, captured inside the branch so the
    // success toast can word itself after the local flip has already happened.
    let archivedDir = null;

    try {
      if (action === 'delete') {
        const result = await API.deleteOpenThreads(ids);
        if (result && result.success !== false) {
          // Remove locally BEFORE broadcasting so this section's own
          // `content:deleted` listener self-skips (bails). This path owns the
          // panel close for a self-originated delete.
          this.openThreads = this.openThreads.filter(t => !idSet.has(t.id));
          targets.forEach(t => emitContentDeleted('thread', t.id));
          if (panelThreadId && idSet.has(panelThreadId) && this._panelOpen) {
            eventBus.emit('right-panel:close');
          }
        } else {
          failed = true;
        }
      } else if (action === 'star') {
        const next = !targets.every(t => t.starredAt);
        const result = await API.starOpenThreads(ids, next);
        if (result && result.success !== false) {
          const stamp = new Date().toISOString();
          for (const t of targets) t.starredAt = next ? stamp : null;
          targets.forEach(t => emitContentStarred('thread', t.id, next));
        } else {
          failed = true;
        }
      } else if (action === 'archive') {
        const next = !targets.every(t => t.dismissedAt);
        const result = await API.archiveOpenThreads(ids, next);
        if (result && result.success !== false) {
          archivedDir = next;
          const stamp = new Date().toISOString();
          for (const t of targets) t.dismissedAt = next ? stamp : null;
          targets.forEach(t => emitContentArchived('thread', t.id, next));
          // A toggled thread that no longer belongs in the current tab leaves it.
          this.openThreads = this.openThreads.filter(t => this.threadMatchesFilter(t));
          if (panelThreadId && idSet.has(panelThreadId)
              && !this.openThreads.some(t => t.id === panelThreadId) && this._panelOpen) {
            eventBus.emit('right-panel:close');
          }
        } else {
          failed = true;
        }
      }
    } finally {
      this._threadActionInFlight = false;
    }
    if (!this.isActive) return;

    if (failed) {
      this.threadActionNotice = 'Some threads couldn’t be updated. Please try again.';
    } else if (archivedDir !== null) {
      // Success toast only on a clean run — the inline notice already speaks
      // for a failed one, and pairing the two would contradict.
      notifyArchived({ count: targets.length, type: 'thread', archived: archivedDir });
    }

    if (this.selectMode) {
      this._exitThreadSelectMode();
    } else {
      // Re-render the list + refresh the bar count (no full bar remount → no flicker).
      this._refreshQuestionsList();
    }
  }

  /**
   * Resolve a thread's per-user `threadNumber` from its UUID and push
   * `?thread=N` to the URL. Push when panel closed; replace when panel
   * open (cross-fade swap; keeps history flat).
   * @param {string} questionId - OpenThread UUID
   * @private
   */
  _navigateToThreadByQuestionId(questionId) {
    if (!questionId) return;
    const thread = this.openThreads.find(t => t.id === questionId);
    const threadNumber = thread?.threadNumber;
    if (typeof threadNumber !== 'number') return;

    // Toggle / dedup — see _navigateToTopic.
    if (this._panelOpen && this._isSelected('thread', questionId)) {
      eventBus.emit('right-panel:close');
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('thread', String(threadNumber));
    url.searchParams.delete('path');
    url.searchParams.delete('topic');
    url.searchParams.delete('threadStatus');
    const target = url.pathname + url.search;
    if (this._panelOpen) {
      page.replace(target);
    } else {
      page(target);
    }
  }

  /**
   * Show a thread detail view in the shared Discovery panel.
   * @param {string} questionId - OpenThread UUID
   * @param {Object} [_options] - Reserved for `{ skipUrlPush }`; the panel
   *   itself does not push URL state, but the flag exists for API symmetry.
   */
  showThreadDetailView(questionId, _options = {}) {
    const question = this.openThreads.find(q => q.id === questionId);
    if (!question) {
      console.warn('[DiscoverySection] Thread not found:', questionId);
      return;
    }

    const payload = this._buildThreadDetailPayload(question);
    this._detailContent = payload.content;
    this.currentSelection = { kind: 'thread', id: questionId };
    this._panelOpen = true;
    this._refreshActiveStates();
    eventBus.emit('right-panel:open', payload);
  }

  /**
   * Build the right-panel payload for a single thread's detail view. Thin
   * wrapper around the shared `buildThreadDetailPayload` (threadDrillHelper.js
   * — also used by session detail's "Threads to pick up" section): this
   * method owns only the Discovery-specific chrome (star/archive/delete,
   * which mutate `this.openThreads` and `this._refreshQuestionsList()`) and
   * routes the session-tag click through `this.handleGoToSession` so that
   * existing bookkeeping stays intact. Pure aside from those closures: it
   * does NOT touch panel/selection state, so both `showThreadDetailView`
   * (fresh open) and `_drillIntoTopicThread` (drill from the topic panel)
   * can reuse it. The `question` object supplies all fields via closure, so
   * a drilled thread need not live in `this.openThreads`.
   *
   * @param {Object} question - Full OpenThread object (Threads-tab or
   *   topic-summary shape; both carry session/star/addressed fields).
   * @returns {Object} A `right-panel:open` / `right-panel:drill` payload.
   * @private
   */
  _buildThreadDetailPayload(question) {
    // Header options dropdown items — rebuilt (not the whole panel) whenever the
    // Archive/Unarchive label needs to flip, via `right-panel:set-options-dropdown`.
    const optionsItems = () => ([
      { action: 'archive', label: question.dismissedAt ? 'Unarchive' : 'Archive' },
      { action: 'delete', label: 'Delete', variant: 'danger' }
    ]);

    const handleArchiveToggle = async () => {
      const next = !question.dismissedAt;
      const result = await API.archiveOpenThreads([question.id], next).catch(() => null);
      if (!result || result.success === false) {
        notifyArchiveFailed({ count: 1, type: 'thread', archived: next });
        return;
      }
      question.dismissedAt = next ? new Date().toISOString() : null;
      emitContentArchived('thread', question.id, next);
      notifyArchived({ count: 1, type: 'thread', archived: next });

      const inList = this.openThreads.some(q => q.id === question.id);
      const shouldBeInList = this.threadMatchesFilter(question);
      if (inList && !shouldBeInList) {
        // The thread left the current tab — drop it and close the panel.
        this.openThreads = this.openThreads.filter(q => q.id !== question.id);
        this._refreshQuestionsList();
        eventBus.emit('right-panel:close');
        return;
      }
      if (!inList && shouldBeInList) {
        this.openThreads = [question, ...this.openThreads];
      }
      this._refreshQuestionsList();
      // Relabel Archive/Unarchive in place (no full-panel reopen).
      eventBus.emit('right-panel:set-options-dropdown', {
        items: optionsItems(),
        onAction: handleOptionAction
      });
    };

    const handleDeleteFromPanel = async () => {
      const confirmed = await modalService.confirm({
        title: 'Delete thread',
        message: 'This thread will be removed. This can’t be undone.',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        variant: 'danger'
      });
      if (!confirmed || !this.isActive) return;
      const result = await API.deleteOpenThreads([question.id]).catch(() => null);
      if (!result || result.success === false) return;
      // Remove locally BEFORE broadcasting (self-delete listener bails); this
      // path owns the panel close.
      this.openThreads = this.openThreads.filter(q => q.id !== question.id);
      emitContentDeleted('thread', question.id);
      this._refreshQuestionsList();
      eventBus.emit('right-panel:close');
    };

    const handleOptionAction = (action) => {
      if (action === 'archive') return handleArchiveToggle();
      if (action === 'delete') return handleDeleteFromPanel();
    };

    return buildThreadDetailPayload(question, {
      onSessionTagClick: (sessionNumber) => this.handleGoToSession(sessionNumber),
      starButton: {
        starred: !!question.starredAt,
        onClick: async (nextStarred) => {
          const result = await API.starOpenThreads([question.id], nextStarred).catch(() => null);
          if (!result || result.success === false) return false;
          question.starredAt = nextStarred ? new Date().toISOString() : null;
          emitContentStarred('thread', question.id, nextStarred);
          this._refreshQuestionsList();
          return true;
        }
      },
      optionsDropdown: {
        items: optionsItems(),
        onAction: handleOptionAction
      }
    });
  }

  /**
   * Handle single question click (Threads-tab row) - navigate to chat.
   * @param {string} questionId - Question ID
   */
  handleQuestionClick(questionId) {
    discussThread(this.openThreads.find(q => q.id === questionId));
  }

  /**
   * Handle session link click - navigate to session notes detail view
   * @param {string} sessionNumber - Session number
   */
  handleGoToSession(sessionNumber) {
    if (!sessionNumber) return;
    // Drills into the session detail in the same right panel if possible
    // (cap = 1 deep). Falls back to a full /search navigate otherwise.
    openSessionInPanel(sessionNumber);
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    return escapeHtml(text);
  }
}
